import React, { useEffect, useState, useMemo, useCallback } from "react";
import InsightButton from "../components/InsightButton";
import { confirm } from "../utils/confirm";
import { esportaXLSX } from "../utils/esportaXLSX";
import XLSXStyle from "xlsx-js-style";
import { collection, query, where, onSnapshot, doc, setDoc, updateDoc, serverTimestamp, addDoc, deleteDoc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { logEvento, TIPI_EVENTO } from "../utils/logEvento";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { Clock, ChevronLeft, ChevronRight, LogIn, LogOut, Printer, Download, AlertTriangle, Edit2, Users, X, Send, Lock } from "lucide-react";
import { usePeriodoLavorativo, giorniAttesiMese, giorniMancanti } from "../utils/periodoLavorativo";
import { useUsaDiario } from "../hooks/useUsaDiario";
import { RegistraAssenzaModal, AssenzeDelMese, useUfficioPersonale } from "../components/AssenzeCartellino";
import { numIt } from "../utils/economiaCommessa";
import { segmentiGiornata } from "../utils/segmentiGiornata";

const MESI = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const GIORNI_SETT = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];
const PAUSA_MINUTI = 60;
const VOCI_SPECIALI = ["Ferie","Permesso","Malattia"];
const COLORI_SPECIALI = { Ferie:"#2563eb", Permesso:"#7c3aed", Malattia:"#dc2626" };

const STATI_PRES = {
  bozza:     { label:"In compilazione", colore:"#6b7280", bg:"#f3f4f6" },
  inviata:   { label:"Inviata",         colore:"#2563eb", bg:"#dbeafe" },
  approvata: { label:"Approvata",       colore:"#059669", bg:"#d1fae5" },
  respinta:  { label:"Respinta",        colore:"#dc2626", bg:"#fee2e2" },
  chiuso_non_verificato: { label:"Chiuso senza verifica", colore:"#d97706", bg:"#fef3c7" },
};

function pad2(n){ return String(n).padStart(2,"0"); }
function oggiStr(){ const d=new Date(); return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`; }
function oraAttuale(){ const d=new Date(); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; }
// Una giornata resta modificabile da chi l'ha compilata fino al giorno successivo compreso;
// dal giorno dopo ancora, si blocca automaticamente — così il dato diventa attendibile in fretta.
// Allineata alla regola Firestore (opzione A): un utente normale può scrivere SOLO sul documento
// del mese corrente — quindi se il mese richiesto non è quello di oggi, il server rifiuterebbe
// comunque la scrittura, e non promettiamo qui una modifica che poi fallirebbe lato server.
function giornoModificabileNormalmente(anno, mese0, giorno){
  const oggi = new Date();
  if (anno !== oggi.getFullYear() || mese0 !== oggi.getMonth()) return false;
  const dataGiorno = new Date(anno, mese0, giorno);
  const domaniLimite = new Date(dataGiorno.getFullYear(), dataGiorno.getMonth(), dataGiorno.getDate()+2); // fine del giorno successivo
  return new Date() < domaniLimite;
}
// Mese "chiuso" = mese precedente a quello corrente (non l'anno/mese di oggi).
function meseChiuso(anno, mese0){
  const oggi = new Date();
  return anno < oggi.getFullYear() || (anno === oggi.getFullYear() && mese0 < oggi.getMonth());
}
// Scadenza verifica: giorno 10 del mese successivo al mese di riferimento. Oltre questa data,
// un cartellino di un mese chiuso e non ancora approvato è considerato "chiuso senza verifica".
// Calcolato live in UI (non scritto su Firestore): non abbiamo ancora Cloud Functions/Blaze per
// un job schedulato che lo persista — quando saranno attive si potrà scrivere lo stato per l'audit.
function scadenzaVerificaSuperata(anno, mese0){
  const scadenza = new Date(anno, mese0+2, 10); // giorno 10 del mese successivo, a mezzanotte
  return new Date() >= scadenza;
}
function minutiDa(hhmm){ if(!hhmm) return null; const [h,m]=hhmm.split(":").map(Number); return h*60+m; }
function fmtOreDecimali(minuti){
  if(minuti==null||isNaN(minuti)) return "—";
  const h = Math.floor(Math.abs(minuti)/60), m = Math.abs(minuti)%60;
  return `${minuti<0?"-":""}${h}h ${pad2(m)}m`;
}
function giorniDelMese(anno, mese){
  const n = new Date(anno, mese+1, 0).getDate();
  return Array.from({length:n},(_,i)=>{
    const d = new Date(anno, mese, i+1);
    return { giorno:i+1, dow:GIORNI_SETT[d.getDay()], isSabDom:d.getDay()===0||d.getDay()===6, dataStr:`${anno}-${pad2(mese+1)}-${pad2(i+1)}` };
  });
}

function calcolaPasqua(anno){
  const a = anno % 19, b = Math.floor(anno/100), c = anno % 100;
  const d = Math.floor(b/4), e = b % 4, f = Math.floor((b+8)/25);
  const g = Math.floor((b-f+1)/3), h = (19*a+b-d-g+15) % 30;
  const i = Math.floor(c/4), k = c % 4, l = (32+2*e+2*i-h-k) % 7;
  const m = Math.floor((a+11*h+22*l)/451);
  const mese = Math.floor((h+l-7*m+114)/31), giorno = ((h+l-7*m+114) % 31) + 1;
  return new Date(anno, mese-1, giorno);
}
function festivitaItaliane(anno){
  const pasqua = calcolaPasqua(anno);
  const pasquetta = new Date(pasqua); pasquetta.setDate(pasqua.getDate()+1);
  const fmt = d => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
  return new Set([
    `${anno}-01-01`, `${anno}-01-06`, fmt(pasqua), fmt(pasquetta),
    `${anno}-04-25`, `${anno}-05-01`, `${anno}-06-02`, `${anno}-08-15`,
    `${anno}-11-01`, `${anno}-12-08`, `${anno}-12-25`, `${anno}-12-26`,
  ]);
}

// Le ore della giornata: segmenti dal calcolo unico (utils/segmentiGiornata.js) — il lavoro
// si accoppia con il lavoro, Ferie/Permesso/Malattia tra loro, e un permesso a ore dentro
// l'orario di lavoro si toglie dalle ore lavorate. Le giornate timbrate in ordine danno gli
// stessi numeri di prima (prove: demo/prove/prova_segmenti.mjs).
function calcolaOreGiorno(timbrature){
  if(!timbrature || timbrature.length===0) return { minutiLavorati:0, minutiSpeciali:0, completo:true, segmenti:[], mancaUscita:false };
  const { segmenti, apertaLavoro, apertaSpeciale } = segmentiGiornata(timbrature);
  const minuti = segmenti.filter(s=>!s.speciale).reduce((t,s)=>t+s.minuti,0);
  const minutiSpec = segmenti.filter(s=>s.speciale).reduce((t,s)=>t+s.minuti,0);
  const completo = !apertaLavoro && !apertaSpeciale;
  // Pausa pranzo detratta solo se le ore lavorate superano 6h (360 minuti)
  // Sotto le 6h l'operaio probabilmente ha fatto un intervento parziale senza pausa
  const minutiNettiLavorati = minuti >= 360 ? Math.max(0, minuti - PAUSA_MINUTI) : minuti;
  // Applica la stessa detrazione, in proporzione, a ciascun segmento non speciale — così il
  // valore mostrato per singola commessa nel cartellino è SEMPRE coerente con il totale
  // giornaliero e con quanto viene contato nel Controllo Economico (stessa fonte, stesso calcolo).
  if (minuti > 0 && minutiNettiLavorati !== minuti) {
    const fattoreRiduzione = minutiNettiLavorati / minuti;
    segmenti.forEach(s => { if (!s.speciale) s.minutiNetti = Math.round(s.minuti * fattoreRiduzione); });
  } else {
    segmenti.forEach(s => { if (!s.speciale) s.minutiNetti = s.minuti; });
  }
  // Calcola minuti per tipo speciale (Ferie, Permesso, Malattia) separatamente
  const minutiPerTipo = {};
  segmenti.filter(s=>s.speciale).forEach(s=>{
    const tipo = s.commessaNome || "Altro";
    minutiPerTipo[tipo] = (minutiPerTipo[tipo]||0) + s.minuti;
  });
  return { minutiLavorati:minutiNettiLavorati, minutiSpeciali:minutiSpec, minutiPerTipo, completo, segmenti, mancaUscita:!completo };
}

// Chi segna le ore sulle commesse nel diario «Le mie ore» (W403): l'entrata va su «Ufficio»
// e non chiede la commessa — il cartellino serve solo per presenze e paghe.
const UFFICIO_DIARIO = "__ufficio_diario";
function ModalTimbra({ tipo, commesse, onConferma, onClose, giornoIniziale=null, bloccaFeriePermesso=false, giorniMancantiCount=0, diario=false }) {
  const oggi = new Date();
  const [giornoSel, setGiornoSel] = useState(giornoIniziale || oggi.getDate());
  const [selVal, setSelVal] = useState(diario ? UFFICIO_DIARIO : "");
  const [ora, setOra] = useState(oraAttuale());
  const [oreSpeciale, setOreSpeciale] = useState("8");
  const [orarioInizioSp, setOrarioInizioSp] = useState("08:00");
  const [orarioFineSp, setOrarioFineSp] = useState("17:00");

  // Giorni del mese corrente fino a oggi incluso (recupero giorni passati dimenticati)
  const giorniDisponibili = useMemo(()=>{
    const arr = [];
    for(let g=1; g<=oggi.getDate(); g++){
      const d = new Date(oggi.getFullYear(), oggi.getMonth(), g);
      arr.push({ giorno:g, label: `${g} ${MESI[oggi.getMonth()]}${g===oggi.getDate()?" (oggi)":""} — ${GIORNI_SETT[d.getDay()]}` });
    }
    return arr.reverse(); // oggi in cima
  },[oggi]);

  const isSpeciale = VOCI_SPECIALI.includes(selVal);

  const commessaSel = commesse.find(c=>c.id===selVal);
  const commessaNome = commessaSel?.name || (isSpeciale ? selVal : "");
  const commessaId = isSpeciale ? "" : selVal;

  const feriePermessoBloccati = isSpeciale && (selVal==="Ferie"||selVal==="Permesso") && bloccaFeriePermesso;

  const conferma = () => {
    if(selVal===""&&tipo==="entrata"){ return; } // blocca solo entrata
    if(feriePermessoBloccati){ return; }
    if(isSpeciale){
      onConferma(orarioInizioSp, "", selVal, { speciale:true, ore:oreSpeciale, orarioFine:orarioFineSp, giorno:giornoSel });
    } else if (selVal === UFFICIO_DIARIO) {
      onConferma(ora, "", "Ufficio", { giorno:giornoSel });
    } else {
      onConferma(ora, commessaId, commessaNome, { giorno:giornoSel });
    }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:380 }}>
        <div style={{ textAlign:"center", marginBottom:16 }}>
          <div style={{ width:56, height:56, borderRadius:"50%", background: tipo==="entrata"?"#d1fae5":"#fee2e2", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 10px" }}>
            {tipo==="entrata" ? <LogIn size={26} color="#059669"/> : <LogOut size={26} color="#dc2626"/>}
          </div>
          <h3 style={{ fontSize:16, fontWeight:800 }}>{tipo==="entrata"?"Timbra Entrata":"Timbra Uscita"}</h3>
        </div>

        <div className="form-group">
          <label className="form-label">Giorno</label>
          <select className="input" value={giornoSel} onChange={e=>setGiornoSel(Number(e.target.value))}
            style={{ fontWeight: giornoSel!==oggi.getDate() ? 700 : 400, color: giornoSel!==oggi.getDate() ? "#d97706" : "#111827" }}>
            {giorniDisponibili.map(g=><option key={g.giorno} value={g.giorno}>{g.label}</option>)}
          </select>
          {giornoSel!==oggi.getDate() && (
            <div style={{ fontSize:10, color:"#d97706", marginTop:4 }}>⚠️ Stai registrando una timbratura per un giorno passato.</div>
          )}
        </div>

        {tipo==="entrata" && (
          <div className="form-group">
            <label className="form-label">Commessa o voce</label>
            <select className="input" value={selVal} onChange={e=>setSelVal(e.target.value)}
              style={{ color: selVal==="" ? "#9ca3af" : "#111827" }}>
              {diario ? (
                <option value={UFFICIO_DIARIO} style={{color:"#111827"}}>🏢 Ufficio</option>
              ) : (<>
              <option value="" disabled style={{color:"#9ca3af"}}>— Seleziona commessa —</option>
              <optgroup label="Commesse">
                {commesse.map(c=><option key={c.id} value={c.id} style={{color:"#111827"}}>{c.codiceCommessa?`[${c.codiceCommessa}] `:""}{c.clienteNome?`${c.clienteNome} — `:""}{c.name}</option>)}
              </optgroup>
              </>)}
              <optgroup label="Voci speciali">
                {VOCI_SPECIALI.map(v=><option key={v} value={v} style={{color:"#111827"}}>{v}</option>)}
              </optgroup>
            </select>
            {diario && selVal===UFFICIO_DIARIO && (
              <div style={{ fontSize:11.5, color:"#5b21b6", marginTop:6, lineHeight:1.45 }}>
                Le ore sulle commesse le segni in <b>⏱️ Le mie ore</b>: il cartellino serve per presenze e paghe.
              </div>
            )}
            {selVal==="" && (
              <div style={{ fontSize:11, color:"#dc2626", marginTop:4, fontWeight:600 }}>
                ⚠️ Seleziona la commessa in cui stai lavorando.
              </div>
            )}
          </div>
        )}
        {feriePermessoBloccati && (
          <div style={{ fontSize:11, color:"#dc2626", marginTop:-8, marginBottom:12, fontWeight:600, background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"8px 12px" }}>
            ⚠️ Non puoi registrare {selVal} finché non hai compilato le presenze dei giorni precedenti mancanti ({giorniMancantiCount}). Completa prima quelle giornate.
          </div>
        )}
        {tipo==="entrata" && isSpeciale ? (
          <>
            <div className="form-group">
              <label className="form-label">Ore di {selVal.toLowerCase()} (calcolate da orario)</label>
              <div style={{ padding:"8px 12px", background:"#f3f4f6", borderRadius:8, fontSize:13, fontWeight:700, color:"#374151" }}>
                {(()=>{
                  const mI = orarioInizioSp.split(":").map(Number); const mF = orarioFineSp.split(":").map(Number);
                  const diff = (mF[0]*60+mF[1]) - (mI[0]*60+mI[1]);
                  return diff>0 ? `${Math.floor(diff/60)}h ${String(diff%60).padStart(2,"0")}m` : "—";
                })()}
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <div className="form-group">
                <label className="form-label">Orario inizio</label>
                <input className="input" type="time" value={orarioInizioSp} onChange={e=>setOrarioInizioSp(e.target.value)}/>
              </div>
              <div className="form-group">
                <label className="form-label">Orario fine</label>
                <input className="input" type="time" value={orarioFineSp} onChange={e=>setOrarioFineSp(e.target.value)}/>
              </div>
            </div>
          </>
        ) : (
          <div className="form-group">
            <label className="form-label">Orario</label>
            <input className="input" type="time" value={ora} onChange={e=>setOra(e.target.value)} style={{ fontSize:24, fontWeight:800, textAlign:"center", padding:"14px" }}/>
          </div>
        )}
        <div style={{ display:"flex", gap:8, marginTop:16 }}>
          <button onClick={onClose} style={{ flex:1, padding:"10px", border:"1px solid #e5e7eb", borderRadius:8, background:"#fff", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>Annulla</button>
          <button onClick={conferma} disabled={feriePermessoBloccati}
            style={{ flex:2, padding:"10px", border:"none", borderRadius:8, background: feriePermessoBloccati?"#d1d5db":(tipo==="entrata"?"#059669":"#dc2626"), color:"#fff", fontWeight:700, cursor: feriePermessoBloccati?"not-allowed":"pointer", fontFamily:"'Inter',sans-serif" }}>
            Conferma {tipo==="entrata"?"Entrata":"Uscita"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalMotivoCorrezioneTardiva({ giorno, onConferma, onClose }) {
  const [motivo, setMotivo] = useState("");
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:420 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:"#92400e" }}>⚠️ Correzione fuori dalla finestra normale</h3>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af" }}><X size={16}/></button>
        </div>
        <div style={{ fontSize:12, color:"#6b7280", marginBottom:14 }}>
          Il giorno {giorno} è già oltre la finestra di modifica normale (compilazione + giorno successivo). Come verificatore puoi comunque intervenire, ma la correzione viene registrata con nome, data e motivo, per restare sempre tracciabile.
        </div>
        <div className="form-group">
          <label className="form-label">Motivo della correzione tardiva *</label>
          <textarea className="input" value={motivo} onChange={e=>setMotivo(e.target.value)}
            placeholder="Es. errore segnalato dall'operaio, verifica incongruenza Giornale di Cantiere..."
            style={{ minHeight:64, resize:"vertical" }}/>
        </div>
        <button onClick={()=>{ if(!motivo.trim())return; onConferma(motivo.trim()); }} className="btn btn-primary" style={{ width:"100%", justifyContent:"center", marginTop:8 }}
          disabled={!motivo.trim()}>
          Conferma e procedi
        </button>
      </div>
    </div>
  );
}

function ModalMotivoRespinta({ onConferma, onClose }) {
  const [motivo, setMotivo] = useState("");
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:420 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:"#991b1b" }}>Respingi cartellino</h3>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af" }}><X size={16}/></button>
        </div>
        <div className="form-group">
          <label className="form-label">Motivo del respingimento *</label>
          <textarea className="input" value={motivo} onChange={e=>setMotivo(e.target.value)}
            placeholder="Es. ore non coerenti con il Giornale di Cantiere, giorni mancanti..."
            style={{ minHeight:64, resize:"vertical" }}/>
        </div>
        <button onClick={()=>{ if(!motivo.trim())return; onConferma(motivo.trim()); }} className="btn btn-primary" style={{ width:"100%", justifyContent:"center", marginTop:8 }}
          disabled={!motivo.trim()}>
          Conferma respingimento
        </button>
      </div>
    </div>
  );
}

function ModalModificaTimb({ timbratura, onSalva, onClose }) {
  const [ora, setOra] = useState(timbratura.ora || "");
  const [motivo, setMotivo] = useState("");
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:380 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <h3 style={{ fontSize:14, fontWeight:700 }}>Modifica {timbratura.tipo==="entrata"?"Entrata":"Uscita"}</h3>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af" }}><X size={16}/></button>
        </div>
        <div style={{ fontSize:11, color:"#9ca3af", marginBottom:12 }}>Orario originale: <strong>{timbratura.ora}</strong></div>
        <div className="form-group">
          <label className="form-label">Nuovo orario</label>
          <input className="input" type="time" value={ora} onChange={e=>setOra(e.target.value)} style={{ fontSize:20, fontWeight:700, textAlign:"center" }}/>
        </div>
        <div className="form-group">
          <label className="form-label">Motivo della modifica *</label>
          <input className="input" value={motivo} onChange={e=>setMotivo(e.target.value)} placeholder="Es. dimenticato di timbrare l'uscita"/>
        </div>
        <button onClick={()=>{ if(!motivo.trim())return; onSalva(ora, motivo); }} className="btn btn-primary" style={{ width:"100%", justifyContent:"center", marginTop:8 }}>
          Salva modifica
        </button>
      </div>
    </div>
  );
}

function CartellinoMensile({ uid, nomeUtente, anno:annoInit, mese:meseInit, orgId, isMio, canVerify, canApprove=false, tolleranzaRitardo, orarioInizioPrevisto, oreLavorativeStandard=8, onBack, addToast, commesse=[], currentUser=null, onUscitaOggi=null, utenti=[], onNavigate=null, isPlatformOwner=false }) {
  const [anno, setAnno] = useState(annoInit);
  const [mese, setMese] = useState(meseInit);
  const [cartData, setCartData] = useState(null);
  const [modificaTimb, setModificaTimb] = useState(null);
  const [modalTimbraCart, setModalTimbraCart] = useState(null);
  const [ultimoGiornoEntrata, setUltimoGiornoEntrata] = useState(null);
  const [richiestaMotivoTardivo, setRichiestaMotivoTardivo] = useState(null); // numero giorno, o null
  const [richiestaMotivoRespinta, setRichiestaMotivoRespinta] = useState(false);
  // Un cartellino deve sempre sapere "a quale dipendente appartiene" per calcolare il costo
  // orario corretto in Controllo Economico — se l'account non è collegato a nessun dipendente,
  // meglio bloccare subito la compilazione con un'indicazione chiara, piuttosto che accumulare
  // ore che poi risultano orfane nei calcoli economici.
  const [dipendenteCollegato, setDipendenteCollegato] = useState(null); // null=verifica in corso, true/false=esito
  useEffect(() => {
    if (!orgId || !uid) return;
    let annullato = false;
    (async () => {
      const snap = await getDocs(query(collection(db,"dipendenti"), where("organizationId","==",orgId), where("nexoraUid","==",uid)));
      if (!annullato) setDipendenteCollegato(!snap.empty);
    })();
    return () => { annullato = true; };
  }, [orgId, uid]);

  // Se l'azione appena eseguita da un verificatore riguarda un giorno fuori dalla finestra
  // normale, apre la richiesta del motivo — la correzione stessa è già stata salvata,
  // qui si registra solo la tracciabilità di chi/quando/perché.
  const richiediMotivoSeTardivo = (giorno) => {
    if (canVerify && !giornoModificabileNormalmente(anno, mese, giorno)) {
      setRichiestaMotivoTardivo(String(giorno));
    }
  };

  const salvaMotivoTardivo = async (motivo) => {
    const giorno = richiestaMotivoTardivo;
    const freshSnap = await getDoc(doc(db,"presenze",docId));
    const giorniAttuali = freshSnap.exists() ? (freshSnap.data().giorni||{}) : {};
    const giornoData = giorniAttuali[giorno] || { timbrature:[] };
    const log = { da: currentUser?.uid||"", nome: currentUser?.displayName||"Admin", quando: new Date().toISOString(), motivo };
    const nuoviGiorni = { ...giorniAttuali, [giorno]: { ...giornoData, correzioniTardive: [...(giornoData.correzioniTardive||[]), log] } };
    await setDoc(doc(db,"presenze",docId), { orgId, uid, anno, mese:mese+1, giorni:nuoviGiorni, updatedAt:serverTimestamp() }, { merge:true });
    setRichiestaMotivoTardivo(null);
    addToast("Correzione tardiva registrata","success");
  };

  const docId = `${orgId}_${uid}_${anno}_${mese+1}`;

  const timbraDirectly = async (tipo, ora, commessaId, commessaNome, extra={}) => {
    const oggi = new Date();
    const giornoTarget = extra.giorno || oggi.getDate();
    const ref = doc(db,"presenze",docId);

    // Costruisci la nuova timbratura
    let nuovaTimbraturaOggetti;
    let toastMsg;

    if(extra.speciale){
      const mInizio = minutiDa(ora), mFine = minutiDa(extra.orarioFine||ora);
      const oreCalcolate = (mFine!=null&&mInizio!=null&&mFine>mInizio) ? (mFine-mInizio)/60 : (parseFloat(extra.ore)||0);
      nuovaTimbraturaOggetti = [
        { tipo:"entrata", ora, commessaId:"", commessaNome, speciale:true, timestamp:new Date().toISOString() },
        { tipo:"uscita", ora:extra.orarioFine||ora, commessaId:"", commessaNome, speciale:true, oreForzate:oreCalcolate, timestamp:new Date().toISOString() }
      ];
      toastMsg = `${commessaNome} registrato ✅`;
    } else {
      nuovaTimbraturaOggetti = [{ tipo, ora, commessaId:commessaId||"", commessaNome:commessaNome||"", timestamp:new Date().toISOString() }];
      toastMsg = tipo==="entrata"?"Entrata registrata ✅":"Uscita registrata ✅";
    }

    // Leggi il documento attuale da Firestore
    const snap = await getDoc(ref);
    let giornoSalvato;
    if(snap.exists()){
      // Documento esiste: aggiungi al giorno esistente
      const giorniAttuali = snap.data().giorni || {};
      const giornoData = giorniAttuali[giornoTarget] || { timbrature:[] };
      const nuoveTimb = [...(giornoData.timbrature||[]), ...nuovaTimbraturaOggetti];
      giornoSalvato = { ...giornoData, timbrature: nuoveTimb };
      const nuoviGiorni = { ...giorniAttuali, [giornoTarget]: giornoSalvato };
      await setDoc(ref, { ...snap.data(), giorni: nuoviGiorni, updatedAt: serverTimestamp() });
    } else {
      // Documento non esiste: crealo da zero
      giornoSalvato = { timbrature: nuovaTimbraturaOggetti };
      const nuoviGiorni = { [giornoTarget]: giornoSalvato };
      await setDoc(ref, { orgId, uid, anno, mese:mese+1, stato:"bozza", giorni: nuoviGiorni, updatedAt: serverTimestamp() });
    }
    sincronizzaMovimentiGiorno(String(giornoTarget), giornoSalvato); // impatto economico immediato, non solo dopo approvazione
    richiediMotivoSeTardivo(giornoTarget);

    addToast(toastMsg,"success");
    setModalTimbraCart(null);
    if (isMio && tipo==="uscita" && !extra.speciale && giornoTarget===oggi.getDate()) {
      onUscitaOggi && onUscitaOggi();
    }
  };

  const giorni = useMemo(()=>giorniDelMese(anno,mese),[anno,mese]);
  const festivi = useMemo(()=>festivitaItaliane(anno),[anno]);

  // Periodo lavorativo (assunzione/cessazione, soggetto a cartellino) e assenze per periodo
  const periodo = usePeriodoLavorativo(orgId, uid);
  const ufficioPersonale = useUfficioPersonale();
  // la persona del cartellino segna le ore sulle commesse nel diario «Le mie ore» (W403)
  const usaDiarioPersona = useUsaDiario(orgId, uid);
  const [mostraRegistraAssenza, setMostraRegistraAssenza] = useState(false);

  // Impostazioni economiche caricate una sola volta all'apertura del cartellino (non a ogni salvataggio)
  const [econCache, setEconCache] = useState(null);
  useEffect(() => {
    if (!orgId || !uid) return;
    let annullato = false;
    (async () => {
      const orgSnap = await getDoc(doc(db,"organizations",orgId));
      const orgData = orgSnap.exists() ? orgSnap.data() : {};
      const fontOre = orgData.fontOreEconomico || "programmazione";
      const costoBase = numIt(orgData.costoOrarioBase) || 0;
      let costoOrario = costoBase;
      if (fontOre === "presenze") {
        const { getDocs: gd, query: q, where: w, collection: c } = await import("firebase/firestore");
        const dipSnap = await gd(q(c(db,"dipendenti"), w("organizationId","==",orgId), w("nexoraUid","==",uid)));
        if (!dipSnap.empty) {
          const dipData = dipSnap.docs[0].data();
          if (dipData.costoOrario && numIt(dipData.costoOrario) > 0) costoOrario = numIt(dipData.costoOrario);
        }
      }
      const commesseByName = {};
      const commesseControlloAttivo = {};
      const { getDocs: gd2, query: q2, where: w2, collection: c2 } = await import("firebase/firestore");
      const projSnaps = await gd2(q2(c2(db,"projects"), w2("organizationId","==",orgId)));
      projSnaps.docs.forEach(d => {
        const data = d.data();
        if (data.name) commesseByName[data.name] = d.id;
        if (data.codiceCommessa) commesseByName[data.codiceCommessa] = d.id;
        commesseControlloAttivo[d.id] = !!data.controlloEconomicoAbilitato;
      });
      if (!annullato) setEconCache({ fontOre, costoOrario, commesseByName, commesseControlloAttivo });
    })();
    return () => { annullato = true; };
  }, [orgId, uid]);

  // Sincronizza i movimenti economici SOLO per la giornata indicata — richiamata a ogni
  // salvataggio (timbratura, modifica, eliminazione), non solo all'approvazione.
  // Stesso ID deterministico di sempre (pres_org_uid_anno_mese_giorno_commessa): sovrascrive,
  // non duplica mai.
  // Le query sui movimenti DEVONO filtrare anche per organizationId: le Regole Firestore
  // (Gruppo B) consentono la lettura solo se la query è vincolata alla propria
  // organizzazione. Senza quel filtro la lettura veniva rifiutata, la funzione si fermava
  // prima di scrivere e — essendo chiamata senza attesa — l'errore restava invisibile:
  // le ore del cartellino non arrivavano mai nel Controllo Economico.
  const sincronizzaMovimentiGiorno = async (giornoStr, giornoData, prefissoMovOverride=null) => {
    try { await sincronizzaMovimentiGiornoInterno(giornoStr, giornoData, prefissoMovOverride); }
    catch (err) {
      console.error("Sincronizzazione ore → Controllo Economico non riuscita:", err);
      if (typeof addToast === "function") addToast("Ore salvate nel cartellino, ma non ancora riportate sul costo della commessa: " + (err?.message || "errore sconosciuto"), "error", 8000);
    }
  };
  const sincronizzaMovimentiGiornoInterno = async (giornoStr, giornoData, prefissoMovOverride=null) => {
    if (!econCache || econCache.fontOre !== "presenze") return;
    // Chi segna le ore nel diario «Le mie ore»: il costo sulle commesse viene da lì, il
    // cartellino non ne crea (e toglie quelli eventualmente già scritti per quel giorno).
    if (usaDiarioPersona) {
      const { getDocs: gdD, query: qD, where: wD, collection: cD, deleteDoc: delD } = await import("firebase/firestore");
      const esistentiSnap = await gdD(qD(cD(db,"movimentiEconomici"), wD("organizationId","==",orgId), wD("presenzeRef","==",`${orgId}_${uid}_${anno}_${mese+1}`), wD("dati.giorno","==",giornoStr)));
      for (const d of esistentiSnap.docs) await delD(d.ref);
      return;
    }
    // Nessun movimento economico a costo zero: se questa persona non è collegata a un
    // Dipendente con un costo orario valido (e non c'è un Costo orario base di ripiego),
    // le sue ore restano tracciate nel cartellino ma NON contabilizzate economicamente —
    // stesso principio della commessa mancante qualche riga sotto. Verrà rimosso ogni
    // eventuale movimento già scritto in precedenza per non lasciare in giro importi a zero.
    if (!(econCache.costoOrario > 0)) {
      const { getDocs: gdZ, query: qZ, where: wZ, collection: cZ, deleteDoc: delZ } = await import("firebase/firestore");
      const esistentiSnap = await gdZ(qZ(cZ(db,"movimentiEconomici"), wZ("organizationId","==",orgId), wZ("presenzeRef","==",`${orgId}_${uid}_${anno}_${mese+1}`), wZ("dati.giorno","==",giornoStr)));
      for (const d of esistentiSnap.docs) await delZ(d.ref);
      return;
    }
    const prefissoMov = prefissoMovOverride || `pres_${orgId}_${uid}_${anno}_${mese+1}`;
    const timb = giornoData?.timbrature || [];
    const r = calcolaOreGiorno(timb);
    const orePerCommessa = {};
    const segmentiScartati = [];
    r.segmenti.filter(s=>!s.speciale).forEach(s=>{
      const chiave = s.commessaNome || "";
      if (!chiave) return;
      const entrataTimbra = timb.find(t=>t.tipo==="entrata"&&t.ora===s.entrata);
      const cId = entrataTimbra?.commessaId || econCache.commesseByName[chiave] || "";
      // Il toggle "Economico ON/OFF" nasconde solo la scheda Controllo Economico nell'interfaccia —
      // non deve bloccare la registrazione del dato: se la commessa lo riattiva in futuro,
      // la storia delle ore deve già esserci, non un buco.
      if (!cId) { segmentiScartati.push(chiave); return; }
      if (!orePerCommessa[cId]) orePerCommessa[cId] = { minuti:0, nome:chiave };
      orePerCommessa[cId].minuti += s.minutiNetti; // già al netto della pausa, stessa fonte del cartellino
    });
    if (segmentiScartati.length>0 && typeof addToast==="function") {
      addToast(`Ore su "${segmentiScartati.join(", ")}" non contabilizzate — commessa non riconosciuta`,"info");
    }
    // Prima rimuovi eventuali movimenti di commesse non più presenti in questa giornata (es. commessa tolta/corretta)
    const { getDocs: gd3, query: q3, where: w3, collection: c3, deleteDoc: del3 } = await import("firebase/firestore");
    const esistentiSnap = await gd3(q3(c3(db,"movimentiEconomici"), w3("organizationId","==",orgId), w3("presenzeRef","==",`${orgId}_${uid}_${anno}_${mese+1}`), w3("dati.giorno","==",giornoStr)));
    for (const d of esistentiSnap.docs) {
      if (!orePerCommessa[d.data().projectId]) await del3(d.ref);
    }
    for (const [cId, dati] of Object.entries(orePerCommessa)) {
      const oreReali = dati.minuti/60;
      const importo = Math.round(oreReali*econCache.costoOrario*100)/100;
      const movId = `${prefissoMov}_${giornoStr}_${cId}`;
      await setDoc(doc(db,"movimentiEconomici",movId), {
        projectId: cId, organizationId: orgId,
        tipo: "costo", categoriaId: "manodopera", categoriaLabel: "Manodopera",
        importo, ore: oreReali,
        dati: { descrizione:`Presenze — ${giornoStr}/${mese+1}/${anno}`, dipendente: nomeUtente, giorno: giornoStr },
        presenzeRef: `${orgId}_${uid}_${anno}_${mese+1}`, createdAt: serverTimestamp(), modificataAt: serverTimestamp()
      });
    }
  };

  useEffect(()=>{
    if(!orgId||!uid) return;
    return onSnapshot(doc(db,"presenze",docId), snap=>{
      setCartData(snap.exists() ? snap.data() : { giorni:{}, stato:"bozza" });
    });
  },[docId,orgId,uid]);

  // Giustificazioni delle timbrature mancanti (W419): un segno sul giorno, con il motivo
  const [giustificazioni, setGiustificazioni] = useState([]);
  useEffect(()=>{
    if(!orgId||!uid) return undefined;
    return onSnapshot(query(collection(db,"giustificazioniPresenze"),where("orgId","==",orgId),where("uid","==",uid)),
      snap=>setGiustificazioni(snap.docs.map(d=>({id:d.id,...d.data()}))), ()=>setGiustificazioni([]));
  },[orgId,uid]);
  const giustDelGiorno = (giorno) => {
    const ds = `${anno}-${pad2(mese+1)}-${pad2(giorno)}`;
    return giustificazioni.filter(x=>x.data===ds).sort((a,b)=>(b.createdAt?.toMillis?.()||0)-(a.createdAt?.toMillis?.()||0))[0] || null;
  };
  const presenze = cartData?.giorni || {};
  const stato = cartData?.stato || "bozza";
  // "Da verificare": mese chiuso e non ancora approvato. Oltre il giorno 10 del mese successivo,
  // mostriamo l'etichetta "chiuso senza verifica" (solo display, nessun blocco — come da decisione).
  const daVerificare = meseChiuso(anno, mese) && stato !== "approvata";
  const statoEffettivo = daVerificare && scadenzaVerificaSuperata(anno, mese) ? "chiuso_non_verificato" : stato;
  // canEdit ora è calcolato per singola giornata (vedi giornoEditabile), non più sull'intero cartellino.
  // Chi verifica/approva può sempre intervenire (correzione tardiva tracciata se fuori dalla finestra normale).
  const giornoEditabile = (giorno) => {
    if (canVerify) return true; // admin/verificatore: sempre, ma loggato come correzione tardiva se fuori finestra
    if (!isMio) return false;
    return giornoModificabileNormalmente(anno, mese, giorno);
  };

  const salvaModifica = async (giorno, idxTimb, nuovaOra, motivo) => {
    const giornoData = presenze[giorno] || { timbrature:[] };
    const timbrature = [...(giornoData.timbrature||[])];
    const originale = timbrature[idxTimb];
    timbrature[idxTimb] = { ...originale, ora:nuovaOra, modificata:true, modificheLog:[...(originale.modificheLog||[]), { da:originale.ora, a:nuovaOra, motivo, quando:new Date().toISOString() }] };
    const giornoSalvato = { ...giornoData, timbrature };
    const nuoviGiorni = { ...presenze, [giorno]: giornoSalvato };
    await setDoc(doc(db,"presenze",docId), { orgId, uid, anno, mese:mese+1, giorni:nuoviGiorni, updatedAt:serverTimestamp() }, { merge:true });
    sincronizzaMovimentiGiorno(String(giorno), giornoSalvato);
    richiediMotivoSeTardivo(giorno);
    addToast("Timbratura corretta ✅","success");
    setModificaTimb(null);
  };

  const eliminaTimbratura = async (giorno, idxTimb) => {
    if (!await confirm("Eliminare questa timbratura?", { type:"danger" })) return;
    const giornoData = presenze[giorno] || { timbrature:[] };
    const timbrature = (giornoData.timbrature||[]).filter((_,i)=>i!==idxTimb);
    const giornoSalvato = { ...giornoData, timbrature };
    const nuoviGiorni = { ...presenze, [giorno]: giornoSalvato };
    await setDoc(doc(db,"presenze",docId), { orgId, uid, anno, mese:mese+1, giorni:nuoviGiorni, updatedAt:serverTimestamp() }, { merge:true });
    sincronizzaMovimentiGiorno(String(giorno), giornoSalvato);
    richiediMotivoSeTardivo(giorno);
    addToast("Timbratura eliminata","success");
  };

  const totaleMese = useMemo(()=>{
    let tot = 0;
    Object.values(presenze).forEach(g=>{ tot += calcolaOreGiorno(g.timbrature||[]).minutiLavorati; });
    return tot;
  },[presenze]);

  const totaliSpeciali = useMemo(()=>{
    const tot = { Ferie:0, Permesso:0, Malattia:0 };
    Object.values(presenze).forEach(g=>{
      const r = calcolaOreGiorno(g.timbrature||[]);
      Object.entries(r.minutiPerTipo||{}).forEach(([tipo,min])=>{
        if(tot[tipo]!=null) tot[tipo]+=min;
      });
    });
    return tot;
  },[presenze]);

  // Totali annui: somma tutti i mesi dell'anno corrente
  const [totaliAnnui, setTotaliAnnui] = useState({ Ferie:0, Permesso:0, Malattia:0 });
  useEffect(()=>{
    if(!orgId||!uid||!anno) return;
    let cancelled = false;
    const caricaAnnui = async () => {
      const tot = { Ferie:0, Permesso:0, Malattia:0 };
      const promises = Array.from({length:12},(_,i)=>{
        const docId2 = `${orgId}_${uid}_${anno}_${i+1}`;
        return getDoc(doc(db,"presenze",docId2));
      });
      const snaps = await Promise.all(promises);
      snaps.forEach(snap=>{
        if(!snap.exists()) return;
        const giorni2 = snap.data().giorni||{};
        Object.values(giorni2).forEach(g=>{
          const r = calcolaOreGiorno(g.timbrature||[]);
          Object.entries(r.minutiPerTipo||{}).forEach(([tipo,min])=>{
            if(tot[tipo]!=null) tot[tipo]+=min;
          });
        });
      });
      if(!cancelled) setTotaliAnnui(tot);
    };
    caricaAnnui();
    return ()=>{ cancelled=true; };
  },[orgId,uid,anno,mese]); // ricarica quando cambia mese

  const giorniLavorati = Object.values(presenze).filter(g=>(g.timbrature||[]).length>0).length;

  // Giorni da compilare: regola unica (utils/periodoLavorativo.js), la stessa della nota
  // spese — solo feriali, solo dentro il periodo di lavoro della persona, fino a ieri, e
  // nessuno per chi non è soggetto a cartellino.
  const validazioneInvio = useMemo(()=>{
    if (!periodo.pronto) return { ok:true, mancanti:[] };
    const mancanti = giorniMancanti(giorniAttesiMese(anno, mese, periodo), presenze);
    return { ok: mancanti.length===0, mancanti };
  },[presenze,anno,mese,periodo]);

  const aggiornaStato = async (nuovoStato, motivoRespinta="") => {
    // Separazione dei compiti: nessuno può approvare o respingere un proprio cartellino,
    // titolare o admin che sia — eccetto il Platform Owner, per poter fare le sue prove.
    // Stessa identica protezione già esistente su Note Spese, mancante qui fino ad oggi.
    const staBypassando = (nuovoStato === "approvata" || nuovoStato === "respinta") && isMio;
    if (staBypassando && !isPlatformOwner) {
      addToast("Non puoi approvare o respingere un tuo stesso cartellino — serve un altro amministratore","error");
      return;
    }
    // Le ore contano già in tempo reale (sincronizzaMovimentiGiorno) fin dalla compilazione —
    // qui aggiorniamo solo l'etichetta di verifica per l'Amministrazione/HR, senza toccare i
    // movimenti economici, che restano quelli già calcolati giorno per giorno.
    await setDoc(doc(db,"presenze",docId), {
      orgId, uid, anno, mese:mese+1, stato:nuovoStato,
      ...(motivoRespinta?{motivoRespinta}:{}),
      updatedAt:serverTimestamp()
    }, { merge:true });
    const tipoLog = nuovoStato==="approvata"?"CARTELLINO_APPROVATO":"CARTELLINO_RESPINTO";
    if(nuovoStato==="approvata"||nuovoStato==="respinta"){
      logEvento({ orgId, tipo:tipoLog, ...TIPI_EVENTO[tipoLog],
        titolo:`Cartellino ${nuovoStato}: ${nomeUtente} — ${MESI[mese]} ${anno}`,
        uid:currentUser?.uid||"", nomeUtente:currentUser?.displayName||"",
        refId:docId, refTipo:"cartellino", refNome:nomeUtente,
        navigateTo:{page:"presenze"}
      });
    }
    addToast(nuovoStato==="approvata"?"Cartellino verificato ✅":nuovoStato==="respinta"?"Cartellino segnalato da correggere":"Stato aggiornato","success");
  };

  // Blocco compilazione se l'account non risulta collegato a nessun dipendente in anagrafica —
  // senza questo collegamento, Controllo Economico non saprebbe mai quale costo orario
  // applicare a queste ore, e resterebbero orfane nei calcoli. Il controllo resta silenzioso
  // (nessun blocco visibile) finché la verifica è ancora in corso (dipendenteCollegato===null).
  if (dipendenteCollegato === false) {
    return (
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", padding:32, textAlign:"center" }}>
        <div style={{ fontSize:40, marginBottom:12 }}>🔒</div>
        <div style={{ fontSize:15, fontWeight:800, color:"#111827", marginBottom:8 }}>
          Cartellino non ancora attivabile
        </div>
        <div style={{ fontSize:13, color:"#6b7280", maxWidth:420, lineHeight:1.6, marginBottom:20 }}>
          Il tuo account non risulta ancora collegato a nessun dipendente in anagrafica.
          Senza questo collegamento, le ore registrate non saprebbero a quale costo orario
          fare riferimento nei calcoli economici delle commesse.
          {canVerify
            ? " Vai in Dipendenti & Collaboratori e collega questa persona al proprio account Nexiora prima di procedere."
            : " Chiedi al tuo Titolare o Amministratore di collegare il tuo account in Dipendenti & Collaboratori — non è un'operazione che puoi fare da solo."}
        </div>
        {onNavigate && canVerify && (
          <button onClick={()=>onNavigate({page:"dipendenti"})}
            style={{ padding:"9px 20px", background:"#7c3aed", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            Vai a Dipendenti &amp; Collaboratori →
          </button>
        )}
        {onBack && (
          <button onClick={onBack}
            style={{ marginTop:12, padding:"7px 16px", background:"none", color:"#6b7280", border:"1px solid #e5e7eb", borderRadius:8, fontSize:12, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            ← Torna indietro
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div style={{ background:"#fff", borderBottom:"1px solid #e5e7eb", padding:"14px 20px", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:10, flexWrap:"wrap" }}>
          {onBack && <button onClick={onBack} style={{ border:"1px solid #e5e7eb", background:"#fff", borderRadius:6, padding:"5px 10px", cursor:"pointer", fontSize:11, fontFamily:"'Inter',sans-serif" }}>← Torna</button>}
          <h2 style={{ fontSize:15, fontWeight:800 }}>🕐 Cartellino — {nomeUtente}</h2>
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            <button onClick={()=>{ if(mese===0){setMese(11);setAnno(a=>a-1);}else setMese(m=>m-1); }} style={{ border:"1px solid #e5e7eb", background:"#fff", borderRadius:6, padding:"3px 8px", cursor:"pointer" }}><ChevronLeft size={13}/></button>
            <span style={{ fontSize:13, fontWeight:700, minWidth:120, textAlign:"center" }}>{MESI[mese]} {anno}</span>
            <button onClick={()=>{ if(mese===11){setMese(0);setAnno(a=>a+1);}else setMese(m=>m+1); }} style={{ border:"1px solid #e5e7eb", background:"#fff", borderRadius:6, padding:"3px 8px", cursor:"pointer" }}><ChevronRight size={13}/></button>
          </div>
          {isMio && stato==="bozza" && (
            <div style={{ display:"flex", gap:6, marginLeft:8 }}>
              <button onClick={()=>setModalTimbraCart("entrata")}
                style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px", border:"none", borderRadius:8, background:"#059669", color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"'Inter',sans-serif", boxShadow:"0 2px 8px rgba(5,150,105,.3)" }}>
                <LogIn size={14}/> Entrata
              </button>
              <button onClick={()=>setModalTimbraCart("uscita")}
                style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px", border:"none", borderRadius:8, background:"#dc2626", color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"'Inter',sans-serif", boxShadow:"0 2px 8px rgba(220,38,38,.3)" }}>
                <LogOut size={14}/> Uscita
              </button>
            </div>
          )}
          <span style={{ padding:"2px 10px", borderRadius:12, fontSize:11, fontWeight:700, background:STATI_PRES[statoEffettivo].bg, color:STATI_PRES[statoEffettivo].colore }}>{STATI_PRES[statoEffettivo].label}</span>

          {canApprove && daVerificare && (
            <div style={{ display:"flex", gap:6 }}>
              <button onClick={()=>aggiornaStato("approvata")}
                style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 12px", border:"1px solid #059669", borderRadius:7, background:"#f0fdf4", color:"#059669", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                ✓ Approva
              </button>
              <button onClick={()=>setRichiestaMotivoRespinta(true)}
                style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 12px", border:"1px solid #dc2626", borderRadius:7, background:"#fef2f2", color:"#dc2626", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                ✕ Respingi
              </button>
            </div>
          )}

          <button onClick={()=>{
            // giorni = array di oggetti {giorno:1..31, dataStr:"YYYY-MM-DD", dow, isSabDom}
            // presenze = oggetto con chiave numerica giorno: {timbrature:[...], tipo?}
            const intestazione=["Data","Giorno","Tipo","Entrata","Uscita","Ore Lavorate","Commessa","Note"];
            let totMinuti=0, totGiorniLavoro=0, totFerie=0, totPermessi=0, totMalattia=0;
            const righe=(giorni||[]).map(g=>{
              const p=presenze[g.giorno]||{};
              const timb=(p.timbrature||[]).sort((a,b)=>(a.ora||"").localeCompare(b.ora||""));
              const primaEntrata=timb.find(t=>t.tipo==="entrata");
              const ultimaUscita=[...timb].reverse().find(t=>t.tipo==="uscita");
              let minGiorno=0, entr=null;
              timb.forEach(t=>{
                if(t.tipo==="entrata") entr=t.ora;
                else if(t.tipo==="uscita"&&entr){
                  const [h1,m1]=entr.split(":").map(Number);
                  const [h2,m2]=t.ora.split(":").map(Number);
                  minGiorno+=(h2*60+m2)-(h1*60+m1);
                  entr=null;
                }
              });
              totMinuti+=minGiorno;
              const oreTot=minGiorno>0?`${Math.floor(minGiorno/60)}h ${minGiorno%60}m`:"—";
              const tipoGiorno=p.tipo||(g.isSabDom?"Weekend":"Lavoro");
              if(tipoGiorno==="Lavoro"&&minGiorno>0) totGiorniLavoro++;
              if(tipoGiorno==="Ferie") totFerie++;
              if(tipoGiorno==="Permesso") totPermessi++;
              if(tipoGiorno==="Malattia") totMalattia++;
              const commessaList=[...new Set(timb.filter(t=>t.tipo==="entrata"&&t.commessaNome).map(t=>t.commessaNome))].join(", ")||"—";
              return [
                g.dataStr,
                g.dow,
                tipoGiorno,
                (primaEntrata?.ora||"—"),
                (ultimaUscita?.ora||"—"),
                oreTot,
                commessaList,
                p.note||""
              ];
            });
            const oreTotali=`${Math.floor(totMinuti/60)}h ${totMinuti%60}m`;
            // Intestazione documento con dati dipendente e periodo
            const header=[
              ["CARTELLINO PRESENZE —", (nomeUtente||"").toUpperCase(),"","","","","",""],
              [`Periodo: ${MESI[mese]} ${anno}`,"","","","","","",""],
              ["","","","","","","",""],
              intestazione,
            ];
            // Riga totali in fondo
            const rigaTotali=[
              "TOTALI","","",
              "","",
              oreTotali,
              `Giorni lavorati: ${totGiorniLavoro}`,
              `Ferie: ${totFerie} | Permessi: ${totPermessi} | Malattia: ${totMalattia}`
            ];
            // Genera Excel con grassetto su titolo, intestazione e totali
            const allRows = [...header,...righe,["","","","","","","",""],rigaTotali];
            const wb2 = XLSXStyle.utils.book_new();
            const ws2 = XLSXStyle.utils.aoa_to_sheet(allRows);
            ws2["!cols"] = [{wch:12},{wch:18},{wch:10},{wch:8},{wch:8},{wch:12},{wch:35},{wch:30}];
            const B = {font:{bold:true}};
            // Riga 1: titolo — A1 e B1 grassetto
            if(ws2["A1"]) ws2["A1"].s = B;
            if(ws2["B1"]) ws2["B1"].s = B;
            // Riga 4: intestazione colonne grassetto
            ["A","B","C","D","E","F","G","H"].forEach(col=>{ const k=col+"4"; if(ws2[k]) ws2[k].s=B; });
            // Ultima riga: TOTALI grassetto
            const nR = allRows.length;
            ["A","B","C","D","E","F","G","H"].forEach(col=>{ const k=col+nR; if(ws2[k]) ws2[k].s=B; });
            XLSXStyle.utils.book_append_sheet(wb2, ws2, "Presenze");
            XLSXStyle.writeFile(wb2, `Presenze_${(nomeUtente||"Dipendente").replace(/\s+/g,"_")}_${MESI[mese]}_${anno}.xlsx`);
          }} style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",border:"1px solid #059669",borderRadius:7,background:"#f0fdf4",color:"#059669",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            📊 Excel
          </button>

          {(isMio || ufficioPersonale) && (
            <button onClick={()=>setMostraRegistraAssenza(true)}
              title="Malattia, ferie o permesso per un periodo: le giornate vengono inserite nel cartellino"
              style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",border:"1px solid #ea580c",borderRadius:7,background:"#fff7ed",color:"#c2410c",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              🩺 Registra assenza
            </button>
          )}

          {/* Ricalcolo del costo sulla commessa per tutti i giorni del mese: ripristina ore
              registrate quando la sincronizzazione non era riuscita (fino a W305 falliva per
              chi non era Platform Owner) o dopo aver collegato il costo orario del dipendente. */}
          {econCache?.fontOre==="presenze" && econCache.costoOrario>0 && (
            <button onClick={async()=>{
              const giorniConOre = Object.entries(presenze).filter(([,g])=>(g?.timbrature||[]).length>0);
              for (const [g, dati] of giorniConOre) await sincronizzaMovimentiGiorno(String(g), dati);
              addToast(giorniConOre.length ? `Costi aggiornati sulle commesse per ${giorniConOre.length} ${giorniConOre.length===1?"giorno":"giorni"}` : "Nessuna ora registrata in questo mese", "success");
            }} title="Ricalcola e riporta sulle commesse il costo della manodopera di tutti i giorni del mese"
              style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",border:"1px solid #7c3aed",borderRadius:7,background:"#f5f3ff",color:"#7c3aed",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              🔄 Aggiorna costi commessa
            </button>
          )}

          {/* "Invia in accettazione" rimosso — con la nuova logica le ore contano già in tempo reale,
              non serve più un passaggio esplicito di invio. Approva/Respingi gestiti nella sezione
              Approvazione Cartellini, sempre disponibili indipendentemente dallo stato. */}
        </div>

        {mostraRegistraAssenza && (
          <RegistraAssenzaModal orgId={orgId} uid={uid} nomePersona={nomeUtente} periodo={periodo} oreGiornata={oreLavorativeStandard}
            perAltri={!isMio} onClose={()=>setMostraRegistraAssenza(false)} addToast={addToast}/>
        )}
        {(isMio || ufficioPersonale) && (
          <AssenzeDelMese orgId={orgId} uid={uid} anno={anno} mese0={mese} puoGestire={ufficioPersonale} addToast={addToast}/>
        )}
        {periodo.pronto && periodo.soggetto === false && (
          <div style={{ background:"#f3f4f6", border:"1px solid #e5e7eb", borderRadius:8, padding:"6px 12px", marginBottom:10, fontSize:11, color:"#4b5563" }}>
            ℹ️ {isMio ? "Non sei soggetto" : `${nomeUtente} non è soggetto`} a cartellino: nessun giorno è obbligatorio (Presenze → Gestisci esclusioni timbratura).
          </div>
        )}
        {periodo.pronto && periodo.soggetto !== false && periodo.fonteInizio === "account" && ufficioPersonale && (
          <div style={{ background:"#f3f4f6", border:"1px solid #e5e7eb", borderRadius:8, padding:"6px 12px", marginBottom:10, fontSize:11, color:"#4b5563" }}>
            ℹ️ Data di assunzione non indicata in Dipendenti &amp; Collaboratori: i giorni si contano dalla creazione dell'account ({periodo.inizio?.toLocaleDateString("it-IT")}).
          </div>
        )}
        {!validazioneInvio.ok && isMio && (
          <div style={{ background:"#fef3c7", border:"1px solid #fde68a", borderRadius:8, padding:"8px 12px", marginBottom:10, fontSize:11, color:"#92400e" }}>
            ⚠️ Giorni non completati: {validazioneInvio.mancanti.join(", ")} — completa ingresso/uscita o segna Ferie/Permesso/Malattia. Finché non compili, le ore di quei giorni non risultano da nessuna parte.
          </div>
        )}

        {econCache && econCache.fontOre==="presenze" && !(econCache.costoOrario>0) && (
          <div style={{ background:"#fef2f2", border:"2px solid #dc2626", borderRadius:8, padding:"10px 14px", marginBottom:10, fontSize:12, color:"#991b1b", display:"flex", alignItems:"flex-start", gap:8 }}>
            <span style={{fontSize:16,flexShrink:0}}>🚫</span>
            <div>
              <strong>Le ore di {nomeUtente} sono registrate ma NON contabilizzate economicamente.</strong><br/>
              Manca un costo orario collegato — le sue ore non compaiono in Controllo Economico e il cartellino non può essere approvato finché questo non è risolto.<br/>
              <strong>Cosa fare:</strong> un amministratore deve andare in <strong>Dipendenti &amp; Collaboratori</strong> e collegare {nomeUtente} a un Dipendente con un Costo orario valido — oppure impostare un <strong>Costo orario base</strong> in Impostazioni Organizzazione come ripiego generale per tutta l'azienda.
            </div>
          </div>
        )}
        {stato==="approvata" && (
          <div style={{ background:"#d1fae5", border:"1px solid #6ee7b7", borderRadius:8, padding:"8px 12px", marginBottom:10, fontSize:11, color:"#065f46", display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
            <Lock size={12}/>
            <span>Cartellino verificato.</span>
            {cartData?.approvataDA && (()=>{
              const u = utenti.find(u=>u.uid===cartData.approvataDA);
              return u ? <span>Verificato da <strong>{u.displayName}</strong>{cartData.approvataAt?.toDate?<span> il {cartData.approvataAt.toDate().toLocaleDateString("it-IT")}</span>:null}</span> : null;
            })()}
          </div>
        )}
        {/* Nota timbratura multipla */}
        <div style={{ marginBottom:8, padding:"8px 14px", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, fontSize:11, color:"#1d4ed8" }}>
          ℹ️ <strong>Timbratura multipla:</strong> quando lasci un cantiere per recarti su un altro, timbra Uscita, poi timbra subito Entrata per il cantiere successivo, a partire dall'orario in cui hai lasciato il precedente.
        </div>

        {statoEffettivo==="chiuso_non_verificato" && canApprove && (
          <div style={{ background:"#fef3c7", border:"1px solid #fde68a", borderRadius:8, padding:"8px 12px", marginBottom:10, fontSize:11, color:"#92400e" }}>
            ⚠️ Mese chiuso da oltre la scadenza di verifica (giorno 10) senza approvazione. Le ore restano comunque valide e contate — questa è solo una segnalazione di ritardo nella verifica.
          </div>
        )}
        {stato==="inviata" && isMio && (
          <div style={{ background:"#dbeafe", border:"1px solid #93c5fd", borderRadius:8, padding:"8px 12px", marginBottom:10, fontSize:11, color:"#1e40af" }}>
            📤 In attesa di verifica — resta comunque modificabile secondo le regole normali.
          </div>
        )}

        <div style={{ display:"flex", gap:16, alignItems:"center", flexWrap:"wrap" }}>
          <span style={{ fontSize:14, fontWeight:700 }}>{MESI[mese]} {anno}</span>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <div style={{ background:"#ede9fe", borderRadius:8, padding:"6px 14px" }}>
              <div style={{ fontSize:9, color:"#7c3aed", fontWeight:700 }}>ORE LAVORATE</div>
              <div style={{ fontSize:15, fontWeight:800, color:"#5b21b6" }}>{fmtOreDecimali(totaleMese)}</div>
            </div>
            <div style={{ background:"#f0f9ff", borderRadius:6, padding:"4px 10px" }}>
              <div style={{ fontSize:8, color:"#0891b2", fontWeight:700 }}>GIORNI LAV.</div>
              <div style={{ fontSize:13, fontWeight:800, color:"#0e7490" }}>{giorniLavorati}</div>
            </div>
            {[
              { k:"Ferie",    bg:"#bfdbfe", col:"#1d4ed8", dark:"#1e3a8a" },
              { k:"Permesso", bg:"#ede9fe", col:"#7c3aed", dark:"#5b21b6" },
              { k:"Malattia", bg:"#fecaca", col:"#dc2626", dark:"#991b1b" },
            ].map(({k,bg,col,dark})=>{
              const min = totaliSpeciali[k]||0;
              const ore = (min/60).toFixed(1);
              return (
                <div key={k} style={{ background:bg, borderRadius:6, padding:"4px 10px", opacity:min>0?1:0.4 }}>
                  <div style={{ fontSize:8, color:col, fontWeight:700 }}>{k.toUpperCase()} MESE</div>
                  <div style={{ fontSize:13, fontWeight:800, color:dark }}>{min>0?ore+"h":"—"}</div>
                </div>
              );
            })}
          </div>
          {/* Riga totali annui — separatore invisibile, nessun testo anno */}
          <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
            {/* separatore visivo sottile */}
            <div style={{ width:1, height:36, background:"#e5e7eb", flexShrink:0 }}/>
            {[
              { k:"Ferie",    bg:"#bfdbfe", col:"#1d4ed8", dark:"#1e3a8a" },
              { k:"Permesso", bg:"#ede9fe", col:"#7c3aed", dark:"#5b21b6" },
              { k:"Malattia", bg:"#fecaca", col:"#dc2626", dark:"#991b1b" },
            ].map(({k,bg,col,dark})=>{
              const min = totaliAnnui[k]||0;
              return (
                <div key={k} style={{ background:bg, borderRadius:6, padding:"4px 10px", opacity:min>0?1:0.4 }}>
                  <div style={{ fontSize:8, color:col, fontWeight:700 }}>{k.toUpperCase()} ANNO</div>
                  <div style={{ fontSize:13, fontWeight:800, color:dark }}>{min>0?(min/60).toFixed(1)+"h":"—"}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ flex:1, overflowY:"auto" }}>
        {/* Tabella a tutta larghezza (max 1000px): prima occupava metà schermo e la colonna
            delle commesse andava a capo parola per parola */}
        <table className="presenze-table" style={{ borderCollapse:"collapse", width:"100%", maxWidth:1000, minWidth:320, tableLayout:"fixed", fontSize:11 }}>
          <colgroup>
            <col style={{ width:26 }}/>
            <col style={{ width:26 }}/>
            <col style={{ width:44 }}/>
            <col style={{ width:44 }}/>
            <col style={{ width:80 }}/>
            <col style={{ width:18 }}/>
            <col style={{ width:36 }}/>
            <col style={{ width:50 }}/>
            <col style={{ width:38 }}/>
            <col style={{ width:32 }}/>
            <col style={{ width:32 }}/>
            <col style={{ width:32 }}/>
            <col style={{ width:36 }}/>
            <col style={{ width:24 }}/>
          </colgroup>
          <thead style={{ position:"sticky", top:0, zIndex:5 }}>
            <tr style={{ background:"#1e1b4b" }}>
              <th style={{ padding:"4px 3px", textAlign:"left", color:"#fff", fontSize:9, fontWeight:700 }} className="col-gg">Gg</th>
              <th style={{ padding:"4px 3px", textAlign:"left", color:"#fff", fontSize:9, fontWeight:700 }} className="col-data">Data</th>
              <th style={{ padding:"4px 3px", textAlign:"center", color:"#d1fae5", fontSize:9, fontWeight:700 }} className="col-ingresso">Ingresso</th>
              <th style={{ padding:"4px 3px", textAlign:"center", color:"#fee2e2", fontSize:9, fontWeight:700 }} className="col-uscita">Uscita</th>
              <th style={{ padding:"4px 3px", textAlign:"left", color:"#c4b5fd", fontSize:9, fontWeight:700 }} className="col-note">Note</th>
              <th style={{ padding:"4px 2px", textAlign:"center", color:"#fff", fontSize:9, fontWeight:700 }} className="col-stato">St</th>
              <th style={{ padding:"4px 3px", textAlign:"right", color:"#fff", fontSize:9, fontWeight:700 }} className="col-lav">Lav.</th>
              <th style={{ padding:"4px 3px", textAlign:"right", color:"#93c5fd", fontSize:9, fontWeight:700 }} className="col-viag">Viag.</th>
              <th style={{ padding:"4px 3px", textAlign:"right", color:"#fcd34d", fontSize:9, fontWeight:700 }} className="col-str">Str.</th>
              <th style={{ padding:"4px 3px", textAlign:"right", color:"#dbeafe", fontSize:9, fontWeight:700 }} className="col-ferie">Ferie</th>
              <th style={{ padding:"4px 3px", textAlign:"right", color:"#e9d5ff", fontSize:9, fontWeight:700 }} className="col-perm">Perm.</th>
              <th style={{ padding:"4px 3px", textAlign:"right", color:"#fee2e2", fontSize:9, fontWeight:700 }} className="col-mal">Mal.</th>
              <th style={{ padding:"4px 3px", textAlign:"right", color:"#6ee7b7", fontSize:9, fontWeight:700 }} className="col-tot">Tot.</th>
              <th style={{ padding:"4px 2px", textAlign:"center", color:"#fff", fontSize:9, fontWeight:700 }} className="col-del"></th>
            </tr>
          </thead>
          <tbody>
            {giorni.map(({giorno,dow,isSabDom,dataStr})=>{
              const g = presenze[giorno];
              const timb = g?.timbrature || [];
              const r = calcolaOreGiorno(timb);
              const isOggi = dataStr===oggiStr();
              const isFestivo = festivi.has(dataStr);
              const primaEntrata = timb.find(t=>t.tipo==="entrata"&&!t.speciale);
              const ritardo = primaEntrata && orarioInizioPrevisto ? (minutiDa(primaEntrata.ora) - minutiDa(orarioInizioPrevisto)) : null;
              const inRitardo = ritardo!=null && ritardo > Number(tolleranzaRitardo||10);

              const bgRow = isOggi ? "#ede9fe" : isFestivo ? "#fef2f2" : isSabDom ? "#faf5ff" : giorno%2===0 ? "#fff" : "#fafafa";
              const colData = isFestivo ? "#dc2626" : isOggi ? "#7c3aed" : isSabDom ? "#7c3aed" : "#374151";

              const segmentiCompleti = r.segmenti.filter(s=>!s.speciale);
              const entrateSenzaUscita = timb
                .filter(t=>t.tipo==="entrata"&&!t.speciale)
                .filter(t=>!segmentiCompleti.some(s=>s.entrata===t.ora))
                .filter(t=>t!==primaEntrata);
              const numeroRighe = Math.max(1, segmentiCompleti.length + entrateSenzaUscita.length);

              return (
                <React.Fragment key={giorno}>
                  <tr style={{ background:bgRow, borderBottom: r.segmenti.filter(s=>!s.speciale).length>1?"none":"1px solid #f3f4f6", lineHeight:1.2 }}>
                    <td rowSpan={numeroRighe} className="col-gg" style={{ padding:"3px 6px", fontWeight:700, fontSize:10, color: isFestivo?"#dc2626":isSabDom?"#7c3aed":"#6b7280", borderBottom:"1px solid #f3f4f6" }}>{dow}</td>
                    <td rowSpan={numeroRighe} className="col-data" style={{ padding:"3px 6px", fontSize:10, fontWeight: isOggi||isFestivo?700:400, color:colData, borderBottom:"1px solid #f3f4f6" }}>{giorno}/{mese+1}{isFestivo && <span title="Festività" style={{marginLeft:2}}>★</span>}</td>
                    {/* Prima riga: primo segmento */}
                    {(()=>{
                      const seg0 = r.segmenti.filter(s=>!s.speciale)[0];
                      const speciali = timb.filter(t=>t.speciale&&t.tipo==="entrata");
                      return (<>
                        <td className="col-ingresso" style={{ padding:"2px 3px", textAlign:"center" }}>
                          {(()=>{
                            const oraIngresso = seg0?.entrata || primaEntrata?.ora;
                            const idxIngresso = oraIngresso ? timb.findIndex(t=>t.tipo==="entrata"&&!t.speciale&&t.ora===oraIngresso) : -1;
                            return oraIngresso ? (
                              <span onClick={()=>giornoEditabile(giorno)&&idxIngresso>=0&&setModificaTimb({giorno,idx:idxIngresso,ora:oraIngresso,tipo:"entrata",commessaNome:seg0?.commessaNome||primaEntrata?.commessaNome})}
                                style={{fontSize:10,fontWeight:700,color:"#059669",cursor:giornoEditabile(giorno)?"pointer":"default",background:"#d1fae5",borderRadius:4,padding:"1px 4px",display:"inline-block"}}>
                                {oraIngresso}
                              </span>
                            ) : <span style={{color:"#d1d5db",fontSize:9}}>—</span>;
                          })()}
                        </td>
                        <td className="col-uscita" style={{ padding:"2px 3px", textAlign:"center" }}>
                          {seg0?.uscita ? <span onClick={()=>giornoEditabile(giorno)&&setModificaTimb({giorno,idx:timb.findIndex(t=>t.tipo==="uscita"&&!t.speciale&&t.ora===seg0.uscita),ora:seg0.uscita,tipo:"uscita",commessaNome:seg0.commessaNome})}
                            style={{fontSize:10,fontWeight:700,color:"#dc2626",cursor:giornoEditabile(giorno)?"pointer":"default",background:"#fee2e2",borderRadius:4,padding:"1px 4px",display:"inline-block"}}>
                            {seg0.uscita}</span>
                          : timb.length>0 ? <span style={{color:"#d97706",fontSize:9}}>…</span> : <span style={{color:"#d1d5db",fontSize:9}}>—</span>}
                        </td>
                        <td className="col-note" style={{ padding:"2px 3px", fontSize:8, color:"#6b7280" }}>
                          {seg0?.commessaNome && seg0.commessaNome.slice(0,10)}
                          {seg0 && !seg0.commessaNome && <span style={{color:"#f59e0b",fontWeight:700,fontSize:8}} title="Nessuna commessa associata — le ore non verranno imputate nel conto economico">⚠️</span>}
                          {speciali.map((t,i)=>{
                            const col=COLORI_SPECIALI[t.commessaNome]||"#7c3aed";
                            return <span key={i} onClick={()=>giornoEditabile(giorno)&&setModificaTimb({giorno,idx:timb.indexOf(t),...t})}
                              style={{fontSize:8,fontWeight:700,color:"#fff",background:col,borderRadius:4,padding:"1px 3px",cursor:giornoEditabile(giorno)?"pointer":"default",marginLeft:2}}>
                              {t.commessaNome?.slice(0,3)||"?"}
                            </span>;
                          })}
                          {seg0 && <span style={{fontSize:8,color:"#059669",marginLeft:4,fontWeight:700}}>{(seg0.minutiNetti/60).toFixed(1)}h</span>}
                        </td>
                      </>);
                    })()}
                  <td className="col-stato" style={{ padding:"3px 4px", textAlign:"center" }}>
                    {inRitardo && <span style={{ fontSize:8, color:"#d97706", fontWeight:700 }}>⏰+{ritardo}m</span>}
                  </td>
                  {/* Ore lavorate */}
                  <td className="col-lav" style={{ padding:"2px 3px", textAlign:"right", fontSize:10, fontWeight:700, color: r.minutiLavorati>0?"#059669":"#d1d5db" }}>
                    {r.minutiLavorati>0 ? (r.minutiLavorati/60).toFixed(1)+"h" : "—"}
                  </td>
                  {/* Ore viaggio — input editabile */}
                  <td className="col-viag" style={{ padding:"2px 4px", textAlign:"right" }}>
                    {giornoEditabile(giorno) ? (
                      <input
                        type="number" min="0" max="12" step="0.5"
                        value={g?.oreViaggio ?? ""}
                        onChange={async e=>{
                          const val = e.target.value==="" ? null : parseFloat(e.target.value);
                          const nuoviGiorni = { ...presenze, [giorno]:{ ...(g||{}), oreViaggio:val } };
                          await setDoc(doc(db,"presenze",docId),{ orgId, uid, anno, mese:mese+1, giorni:nuoviGiorni, updatedAt:serverTimestamp() },{ merge:true });
                        }}
                        style={{ width:36, fontSize:10, textAlign:"right", border:"1px solid #e5e7eb", borderRadius:4, padding:"1px 3px", fontFamily:"'Inter',sans-serif", color:"#2563eb" }}
                        placeholder="0"
                      />
                    ) : (
                      <span style={{ fontSize:10, fontWeight:700, color:g?.oreViaggio>0?"#2563eb":"#d1d5db" }}>
                        {g?.oreViaggio>0 ? g.oreViaggio.toFixed(1) : "—"}
                      </span>
                    )}
                  </td>
                  {/* Ore straordinarie */}
                  {(()=>{
                    const minStd = oreLavorativeStandard * 60;
                    const isWeekend = isSabDom || isFestivo;
                    const minStraord = isWeekend
                      ? r.minutiLavorati  // tutto il giorno è straordinario
                      : Math.max(0, r.minutiLavorati - minStd); // solo sopra soglia
                    return (
                      <td className="col-str" style={{ padding:"3px 4px", textAlign:"right", fontSize:10, fontWeight:700, color: minStraord>0?"#d97706":"#d1d5db" }}>
                        {minStraord>0 ? fmtOreDecimali(minStraord) : "—"}
                      </td>
                    );
                  })()}
                  {/* Ferie / Permesso / Malattia — distingue per tipo */}
                  {["Ferie","Permesso","Malattia"].map(tipo=>{
                    const min = r.minutiPerTipo?.[tipo]||0;
                    const colori = { Ferie:"#1d4ed8", Permesso:"#5b21b6", Malattia:"#b91c1c" };
                    const bgColori = { Ferie:"#eff6ff", Permesso:"#faf5ff", Malattia:"#fff1f2" };
                    return (
                      <td key={tipo} className={{ Ferie:"col-ferie", Permesso:"col-perm", Malattia:"col-mal" }[tipo]} style={{ padding:"3px 3px", textAlign:"right", fontSize:10, fontWeight:700,
                        color: min>0?colori[tipo]:"#d1d5db",
                        background: min>0?bgColori[tipo]:"transparent" }}>
                        {min>0 ? (min/60).toFixed(1)+"h" : "—"}
                      </td>
                    );
                  })}
                  {/* Ore totali = lavorate + viaggio */}
                  {(()=>{
                    const oreViaggioMin = (g?.oreViaggio||0)*60;
                    const totMin = r.minutiLavorati + oreViaggioMin;
                    return (
                      <td className="col-tot" style={{ padding:"3px 3px", textAlign:"right", fontSize:10, fontWeight:800, color: totMin>0?"#059669":"#d1d5db", background: totMin>0?"#f0fdf4":"transparent" }}>
                        {totMin>0 ? (totMin/60).toFixed(1)+"h" : "—"}
                      </td>
                    );
                  })()}
                  <td rowSpan={numeroRighe} className="col-del" style={{ padding:"3px 4px", textAlign:"center", borderBottom:"1px solid #f3f4f6" }}>
                    {giornoEditabile(giorno) && timb.length>0 && (
                      <button onClick={async ()=>{
                        if (!await confirm(`Cancellare tutte le timbrature del giorno ${giorno}/${mese+1}?`, { type:"danger" })) return;
                        const giornoSalvato = { timbrature:[] };
                        const nuoviGiorni = { ...presenze, [giorno]: giornoSalvato };
                        await setDoc(doc(db,"presenze",docId), { orgId, uid, anno, mese:mese+1, giorni:nuoviGiorni, updatedAt:serverTimestamp() }, { merge:true });
                        sincronizzaMovimentiGiorno(String(giorno), giornoSalvato);
    richiediMotivoSeTardivo(giorno);
                      }}
                        style={{ background:"none", border:"1px solid #fca5a5", borderRadius:5, padding:"1px 5px", cursor:"pointer", color:"#dc2626", fontSize:11, fontWeight:700, lineHeight:1 }}
                        title="Cancella giornata">×</button>
                    )}
                    {giustDelGiorno(giorno) && (()=>{ const gz = giustDelGiorno(giorno); return (
                      <span title={`Giustificazione ${gz.stato}: «${gz.motivo}»${gz.decisoDaNome?` — ${gz.decisoDaNome}`:""}${gz.notaDecisione?`: «${gz.notaDecisione}»`:""}`}
                        style={{ marginLeft:4, fontSize:10, fontWeight:700, cursor:"help", color: gz.stato==="accettata"?"#047857":gz.stato==="respinta"?"#b91c1c":"#92400e" }}>
                        📝{gz.stato==="accettata"?"✓":gz.stato==="respinta"?"✗":"…"}
                      </span>); })()}
                    {g?.correzioniTardive?.length > 0 && (
                      <span title={g.correzioniTardive.map(c=>`${new Date(c.quando).toLocaleDateString("it-IT")} — ${c.nome}: ${c.motivo}`).join("\n")}
                        style={{ marginLeft:4, fontSize:10, color:"#d97706", cursor:"help" }}>
                        ⚠️{g.correzioniTardive.length}
                      </span>
                    )}
                  </td>
                </tr>
                {/* Righe extra per segmenti aggiuntivi (multi-cantiere) + entrate aperte */}
                {(()=>{
                  // Segmenti completi dal 2° in poi + entrate senza uscita (esclusa la prima già mostrata)

                  const righePiuEntrate = [
                    ...segmentiCompleti.slice(1).map(s=>({ tipo:"segmento", seg:s })),
                    ...entrateSenzaUscita.map(t=>({ tipo:"entrata_aperta", t }))
                  ];
                  return righePiuEntrate.map((item, si)=>(
                  <tr key={`${giorno}-seg-${si}`} style={{ background:bgRow, borderBottom: si===righePiuEntrate.length-1?"1px solid #f3f4f6":"none" }}>
                    <td style={{ padding:"2px 3px", textAlign:"center" }}>
                      {item.tipo==="segmento" ? (
                        <span onClick={()=>giornoEditabile(giorno)&&setModificaTimb({giorno,idx:timb.findIndex(t=>t.tipo==="entrata"&&!t.speciale&&t.ora===item.seg.entrata),ora:item.seg.entrata,tipo:"entrata",commessaNome:item.seg.commessaNome})}
                          style={{fontSize:10,fontWeight:700,color:"#059669",cursor:giornoEditabile(giorno)?"pointer":"default",background:"#d1fae5",borderRadius:4,padding:"1px 4px",display:"inline-block"}}>
                          {item.seg.entrata}
                        </span>
                      ) : (
                        <span onClick={()=>giornoEditabile(giorno)&&setModificaTimb({giorno,idx:timb.findIndex(t=>t.tipo==="entrata"&&!t.speciale&&t.ora===item.t.ora),ora:item.t.ora,tipo:"entrata",commessaNome:item.t.commessaNome})}
                          style={{fontSize:10,fontWeight:700,color:"#059669",cursor:giornoEditabile(giorno)?"pointer":"default",background:"#d1fae5",borderRadius:4,padding:"1px 4px",display:"inline-block"}}>
                          {item.t.ora}
                        </span>
                      )}
                    </td>
                    <td style={{ padding:"2px 3px", textAlign:"center" }}>
                      {item.tipo==="segmento" && item.seg.uscita && (
                        <span onClick={()=>giornoEditabile(giorno)&&setModificaTimb({giorno,idx:timb.findIndex(t=>t.tipo==="uscita"&&!t.speciale&&t.ora===item.seg.uscita),ora:item.seg.uscita,tipo:"uscita",commessaNome:item.seg.commessaNome})}
                          style={{fontSize:10,fontWeight:700,color:"#dc2626",cursor:giornoEditabile(giorno)?"pointer":"default",background:"#fee2e2",borderRadius:4,padding:"1px 4px",display:"inline-block"}}>
                          {item.seg.uscita}
                        </span>
                      )}
                      {item.tipo==="entrata_aperta" && <span style={{color:"#d97706",fontSize:9}}>…</span>}
                    </td>
                    <td style={{ padding:"2px 3px", fontSize:8, color:"#6b7280" }}>
                      {item.tipo==="segmento" && item.seg.commessaNome && item.seg.commessaNome.slice(0,10)}
                      {item.tipo==="segmento" && <span style={{fontSize:8,color:"#059669",marginLeft:4,fontWeight:700}}>{(item.seg.minutiNetti/60).toFixed(1)}h</span>}
                      {item.tipo==="entrata_aperta" && item.t.commessaNome && item.t.commessaNome.slice(0,10)}
                    </td>
                    {/* Colonne numeriche vuote — già calcolate nella riga principale */}
                    <td/><td/><td/><td/><td/><td/><td/><td/>
                  </tr>
                  ));
                })()}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background:"#1e1b4b" }}>
              <td colSpan={6} style={{ padding:"10px 6px", fontWeight:800, color:"#a5b4fc", fontSize:11 }}>TOTALE MESE</td>
              <td className="col-lav" style={{ padding:"10px 6px", textAlign:"right", fontWeight:800, color:"#6ee7b7", fontSize:12 }}>
                {(totaleMese/60).toFixed(1)}h
              </td>
              <td className="col-viag" style={{ padding:"10px 6px", textAlign:"right", fontWeight:800, color:"#93c5fd", fontSize:12 }}>
                {(()=>{
                  const tot = Object.values(presenze).reduce((s,g)=>s+(g?.oreViaggio||0),0);
                  return tot>0 ? tot.toFixed(1)+"h" : "—";
                })()}
              </td>
              <td className="col-str" style={{ padding:"10px 6px", textAlign:"right", fontWeight:800, color:"#fcd34d", fontSize:12 }}>
                {(()=>{
                  const minStd = oreLavorativeStandard*60;
                  let totStraord = 0;
                  giorni.forEach(({giorno,isSabDom,dataStr})=>{
                    const g = presenze[giorno];
                    const r = calcolaOreGiorno(g?.timbrature||[]);
                    const isFest = festivi.has(dataStr);
                    totStraord += (isSabDom||isFest) ? r.minutiLavorati : Math.max(0,r.minutiLavorati-minStd);
                  });
                  return totStraord>0 ? (totStraord/60).toFixed(1)+"h" : "—";
                })()}
              </td>
              {["Ferie","Permesso","Malattia"].map(tipo=>{
                const min = totaliSpeciali[tipo]||0;
                const colori = { Ferie:"#93c5fd", Permesso:"#c4b5fd", Malattia:"#fca5a5" };
                return (
                  <td key={tipo} className={{ Ferie:"col-ferie", Permesso:"col-perm", Malattia:"col-mal" }[tipo]} style={{ padding:"10px 6px", textAlign:"right", fontWeight:800, color:min>0?colori[tipo]:"#4b5563", fontSize:12 }}>
                    {min>0 ? (min/60).toFixed(1)+"h" : "—"}
                  </td>
                );
              })}
              <td className="col-tot" style={{ padding:"10px 6px", textAlign:"right", fontWeight:800, color:"#6ee7b7", fontSize:12 }}>
                {(()=>{
                  const totViaggio = Object.values(presenze).reduce((s,g)=>s+(g?.oreViaggio||0)*60,0);
                  return ((totaleMese+totViaggio)/60).toFixed(1)+"h";
                })()}
              </td>
              <td className="col-del"/>
            </tr>
          </tfoot>
        </table>
        <div style={{ padding:"10px 20px", fontSize:10, color:"#9ca3af", fontStyle:"italic" }}>
          Nota: 1 ora di pausa pranzo detratta automaticamente dalle giornate ≥6h lavorate. Straordinari: ore oltre la soglia standard ({oreLavorativeStandard}h/giorno) nei giorni feriali; tutte le ore nei sabati, domeniche e festivi. Il campo Viaggio è editabile — inserire ore di trasferimento cantiere. Le date in rosso con ★ sono festività italiane.
        </div>
      </div>

      {modificaTimb && giornoEditabile(modificaTimb.giorno) && (
        <ModalModificaTimb timbratura={modificaTimb}
          onSalva={(nuovaOra, motivo)=>salvaModifica(modificaTimb.giorno, modificaTimb.idx, nuovaOra, motivo)}
          onClose={()=>setModificaTimb(null)}/>
      )}
      {richiestaMotivoTardivo && (
        <ModalMotivoCorrezioneTardiva giorno={richiestaMotivoTardivo}
          onConferma={salvaMotivoTardivo}
          onClose={()=>setRichiestaMotivoTardivo(null)}/>
      )}
      {richiestaMotivoRespinta && (
        <ModalMotivoRespinta
          onConferma={(motivo)=>{ aggiornaStato("respinta", motivo); setRichiestaMotivoRespinta(false); }}
          onClose={()=>setRichiestaMotivoRespinta(false)}/>
      )}
      {modalTimbraCart && (
        <ModalTimbra tipo={modalTimbraCart} commesse={commesse} diario={!!usaDiarioPersona}
          giornoIniziale={modalTimbraCart==="uscita" ? ultimoGiornoEntrata : null}
          onConferma={(ora,cId,cNome,extra)=>{
            if(modalTimbraCart==="entrata") setUltimoGiornoEntrata(extra.giorno||new Date().getDate());
            timbraDirectly(modalTimbraCart,ora,cId,cNome,extra);
          }}
          onClose={()=>setModalTimbraCart(null)}
          bloccaFeriePermesso={isMio && !validazioneInvio.ok}
          giorniMancantiCount={isMio ? validazioneInvio.mancanti.length : 0}/>
      )}
    </div>
  );
}

export default function PresenzePage({ onNavigate }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const canVerify = isOrgAdmin || isPlatformOwner;
  const [approvatori, setApprovatori] = useState([]);

  const oggi = new Date();
  const [anno, setAnno] = useState(oggi.getFullYear());
  const [mese, setMese] = useState(oggi.getMonth());
  const [utenti, setUtenti] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [orgSettings, setOrgSettings] = useState({ tolleranzaRitardo:10, orarioInizioPrevisto:"08:00", oreLavorativeStandard:8 });
  const [esclusi, setEsclusi] = useState([]);
  const [presenzaOggi, setPresenzaOggi] = useState(null);
  const [statoOggi, setStatoOggi] = useState("bozza");
  const [modalTimbra, setModalTimbra] = useState(null);
  const [showPromemoriaDDT, setShowPromemoriaDDT] = useState(false);
  const [vistaCartellino, setVistaCartellino] = useState(null);
  const [showGestioneEsclusioni, setShowGestioneEsclusioni] = useState(false);
  const [showPannelloVerifica, setShowPannelloVerifica] = useState(false);
  const [tuttePresenzeOrg, setTuttePresenzeOrg] = useState([]);

  // canApprove include admin/owner + approvatori autorizzati
  const canApprove = canVerify || approvatori.includes(currentUser?.uid);

  // Tutti i cartellini dell'org (solo per chi verifica) — serve per il pannello "da verificare".
  // Lettura dell'intera collection filtrata per org: stesso pattern già usato altrove in Nexiora
  // per collection di dimensioni contenute (numero dipendenti × mesi).
  useEffect(()=>{
    if(!effectiveOrgId||!canApprove) return;
    return onSnapshot(query(collection(db,"presenze"),where("orgId","==",effectiveOrgId)), snap=>{
      setTuttePresenzeOrg(snap.docs.map(d=>({id:d.id,...d.data()})));
    });
  },[effectiveOrgId,canApprove]);

  const cartelliniDaVerificare = useMemo(()=>{
    if(!canApprove) return [];
    return tuttePresenzeOrg
      .filter(p=>meseChiuso(p.anno, (p.mese||1)-1) && p.stato!=="approvata")
      .map(p=>({ ...p, scaduto: scadenzaVerificaSuperata(p.anno,(p.mese||1)-1) }))
      .sort((a,b)=> a.anno!==b.anno ? b.anno-a.anno : (b.mese-a.mese) || (b.scaduto - a.scaduto));
  },[tuttePresenzeOrg,canApprove]);

  useEffect(()=>{ if(!effectiveOrgId) return; return onSnapshot(doc(db,"organizations",effectiveOrgId), snap=>{ if(snap.exists()) setOrgSettings(snap.data()); }); },[effectiveOrgId]);
  useEffect(()=>{ if(!effectiveOrgId) return; return onSnapshot(doc(db,"presenzeApprovatori",effectiveOrgId), snap=>{ setApprovatori(snap.exists()?(snap.data().uids||[]):[]); }); },[effectiveOrgId]);
  useEffect(()=>{ if(!effectiveOrgId) return; return onSnapshot(query(collection(db,"users"),where("organizationId","==",effectiveOrgId),where("enabled","==",true)), snap=>{ const l=snap.docs.map(d=>d.data()); l.sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||"","it")); setUtenti(l); }); },[effectiveOrgId]);
  useEffect(()=>{ if(!effectiveOrgId) return; return onSnapshot(query(collection(db,"projects"),where("organizationId","==",effectiveOrgId)), snap=>setCommesse(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived && !p.isParent && p.statoCommessa!=="chiusa"))); },[effectiveOrgId]);
  useEffect(()=>{ if(!effectiveOrgId) return; return onSnapshot(doc(db,"presenzeEsclusioni",effectiveOrgId), snap=>{ setEsclusi(snap.exists() ? (snap.data().uids||[]) : []); }); },[effectiveOrgId]);

  const [giorniMeseCorrente, setGiorniMeseCorrente] = useState({});
  const docIdOggi = `${effectiveOrgId}_${currentUser?.uid}_${oggi.getFullYear()}_${oggi.getMonth()+1}`;
  useEffect(()=>{
    if(!effectiveOrgId||!currentUser?.uid) return;
    return onSnapshot(doc(db,"presenze",docIdOggi), snap=>{
      const data = snap.exists() ? snap.data() : {};
      const giorni = data.giorni||{};
      setPresenzaOggi(giorni[oggi.getDate()] || { timbrature:[] });
      setStatoOggi(data.stato || "bozza");
      setGiorniMeseCorrente(giorni);
    });
  },[docIdOggi,effectiveOrgId,currentUser]);

  // Giorni feriali (non weekend, non festivi) del mese in corso, fino a ieri incluso, senza
  // nessuna timbratura — serve per bloccare Ferie/Permesso finché non si è "in regola".
  // Stessa regola unica del cartellino e della nota spese (utils/periodoLavorativo.js)
  const periodoMio = usePeriodoLavorativo(effectiveOrgId, currentUser?.uid);
  const mancantiPrecedenti = useMemo(()=>{
    if (!periodoMio.pronto) return [];
    return giorniMancanti(giorniAttesiMese(oggi.getFullYear(), oggi.getMonth(), periodoMio, oggi), giorniMeseCorrente);
  },[giorniMeseCorrente,oggi,periodoMio]);
  const bloccaFeriePermesso = mancantiPrecedenti.length>0;

  const sonoEscluso = esclusi.includes(currentUser?.uid);
  // segno le ore sulle commesse nel diario «Le mie ore»: l'entrata va su «Ufficio» (W403)
  const usaDiarioMio = useUsaDiario(effectiveOrgId, currentUser?.uid);
  const cartellinoBloccato = statoOggi !== "bozza";

  const timbra = async (tipo, ora, commessaId, commessaNome, extra={}) => {
    if(cartellinoBloccato){
      addToast("Cartellino già inviato/approvato — non modificabile.","error");
      setModalTimbra(null);
      return;
    }
    const docId = `${effectiveOrgId}_${currentUser.uid}_${oggi.getFullYear()}_${oggi.getMonth()+1}`;
    const ref = doc(db,"presenze",docId);
    const giornoTarget = extra.giorno || oggi.getDate();

    // Leggi sempre dati freschi dal documento per evitare di perdere timbrature
    // se il giorno selezionato è diverso da quello in cache locale (presenzaOggi)
    const snap = await getDoc(ref);
    const giorniAttuali = snap.exists() ? (snap.data().giorni || {}) : {};
    const timbCorrente = giorniAttuali[giornoTarget]?.timbrature || [];

    const giornoData = giorniAttuali[giornoTarget] || { timbrature:[] };
    let nuoveTimb;

    if(extra.speciale){
      const mInizio = minutiDa(ora), mFine = minutiDa(extra.orarioFine||ora);
      const oreCalcolate = (mFine!=null && mInizio!=null && mFine>mInizio) ? (mFine-mInizio)/60 : (parseFloat(extra.ore)||0);
      const entrata = { tipo:"entrata", ora:ora, commessaId:"", commessaNome, speciale:true, timestamp:new Date().toISOString() };
      const uscita = { tipo:"uscita", ora:extra.orarioFine||ora, commessaId:"", commessaNome, speciale:true, oreForzate:oreCalcolate, timestamp:new Date().toISOString() };
      nuoveTimb = [...timbCorrente, entrata, uscita];
      addToast(`${commessaNome} registrato ✅`,"success");
    } else {
      const nuova = { tipo, ora, commessaId:commessaId||"", commessaNome:commessaNome||"", timestamp:new Date().toISOString() };
      nuoveTimb = [...timbCorrente, nuova];
      addToast(tipo==="entrata" ? "Entrata registrata ✅" : "Uscita registrata ✅", "success");
    }

    // Salva — leggi prima il documento per non perdere altri giorni
    const snapV = await getDoc(ref);
    if(snapV.exists()){
      const giorniEsistenti = snapV.data().giorni || {};
      const giornoDataV = giorniEsistenti[giornoTarget] || { timbrature:[] };
      const nuoveTimbV = [...(giornoDataV.timbrature||[]), ...nuoveTimb.slice(timbCorrente.length)];
      const nuoviGiorni = { ...giorniEsistenti, [giornoTarget]: { ...giornoDataV, timbrature: nuoveTimbV } };
      await setDoc(ref, { ...snapV.data(), giorni: nuoviGiorni, updatedAt: serverTimestamp() });
    } else {
      const nuoviGiorni = { [giornoTarget]: { timbrature: nuoveTimb } };
      await setDoc(ref, { orgId: effectiveOrgId, uid: currentUser.uid, anno: oggi.getFullYear(), mese: oggi.getMonth()+1, stato:"bozza", giorni: nuoviGiorni, updatedAt: serverTimestamp() });
    }
    setModalTimbra(null);
    attivaPromemoriaDDT(tipo, extra);
  };

  const attivaPromemoriaDDT = (tipo, extra={}) => {
    if (tipo!=="uscita" || extra.speciale) return;
    if (extra.giorno && extra.giorno!==oggi.getDate()) return; // solo per il giorno di oggi
    const chiave = `nexiora_promemoria_ddt_${currentUser.uid}`;
    const ultimaVolta = localStorage.getItem(chiave);
    const giorniPassati = ultimaVolta ? (Date.now()-parseInt(ultimaVolta,10))/(1000*60*60*24) : 99;
    if (giorniPassati >= 3) {
      setShowPromemoriaDDT(true);
      localStorage.setItem(chiave, Date.now().toString());
    }
  };

  const toggleEsclusione = async (uid) => {
    const nuovi = esclusi.includes(uid) ? esclusi.filter(x=>x!==uid) : [...esclusi, uid];
    await setDoc(doc(db,"presenzeEsclusioni",effectiveOrgId), { uids:nuovi, organizationId:effectiveOrgId }, { merge:true });
  };

  const ultimaTimb = (presenzaOggi?.timbrature||[])[(presenzaOggi?.timbrature||[]).length-1];
  const prossimaAzione = !ultimaTimb || ultimaTimb.tipo==="uscita" ? "entrata" : "uscita";

  // Apri sempre direttamente il cartellino dell'utente corrente
  // Admin/owner possono tornare indietro e vedere gli altri dalla lista
  if (!vistaCartellino && !sonoEscluso) {
    const uid2 = currentUser?.uid;
    const nome2 = utenti.find(u=>u.uid===uid2)?.displayName || "";
    return (
      <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
        <CartellinoMensile uid={uid2} nomeUtente={nome2} anno={anno} mese={mese} orgId={effectiveOrgId}
          isMio={true} canVerify={canVerify} canApprove={canApprove} commesse={commesse}
          tolleranzaRitardo={orgSettings.tolleranzaRitardo} orarioInizioPrevisto={orgSettings.orarioInizioPrevisto}
          onBack={canVerify ? ()=>setVistaCartellino("lista") : null} addToast={addToast} currentUser={currentUser} utenti={utenti}
          onUscitaOggi={()=>attivaPromemoriaDDT("uscita",{})} onNavigate={onNavigate} isPlatformOwner={isPlatformOwner}/>

      </div>
    );
  }

  if (vistaCartellino && vistaCartellino!=="lista") {
    return (
      <CartellinoMensile uid={vistaCartellino.uid} nomeUtente={vistaCartellino.nome} anno={anno} mese={mese} orgId={effectiveOrgId}
        isMio={vistaCartellino.uid===currentUser?.uid} canVerify={canVerify} canApprove={canApprove} commesse={commesse}
        tolleranzaRitardo={orgSettings.tolleranzaRitardo} orarioInizioPrevisto={orgSettings.orarioInizioPrevisto}
        onBack={()=>setVistaCartellino(null)} addToast={addToast} utenti={utenti}
        onUscitaOggi={()=>attivaPromemoriaDDT("uscita",{})} onNavigate={onNavigate} isPlatformOwner={isPlatformOwner}/>
    );
  }

  const utentiAttivi = utenti.filter(u=>!esclusi.includes(u.uid));

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div style={{ background:"#fff", borderBottom:"1px solid #e5e7eb", padding:"14px 20px", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
          <h2 style={{ fontSize:16, fontWeight:800, display:"flex", alignItems:"center", gap:8 }}>
            <Clock size={17} color="#7c3aed"/> Presenze
            {canApprove && cartelliniDaVerificare.length>0 && (
              <button onClick={()=>setShowPannelloVerifica(true)} style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 10px", border:"1px solid #fde68a", borderRadius:7, background:"#fffbeb", color:"#92400e", cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:"'Inter',sans-serif" }}>
                <AlertTriangle size={12}/> {cartelliniDaVerificare.length} da verificare
              </button>
            )}
            {canVerify && (
              <button onClick={()=>setShowGestioneEsclusioni(true)} style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 10px", border:"1px solid #e5e7eb", borderRadius:7, background:"#fff", color:"#374151", cursor:"pointer", fontSize:11, fontFamily:"'Inter',sans-serif" }}>
                <Users size={12}/> Gestisci esclusioni
              </button>
            )}
            <InsightButton onNavigate={onNavigate}/>
          </h2>
        </div>

        {!sonoEscluso && (
          <div style={{ background: cartellinoBloccato ? "#f3f4f6" : "linear-gradient(135deg,#faf5ff,#fff)", border: cartellinoBloccato ? "1px solid #e5e7eb" : "1px solid #e9d5ff", borderRadius:12, padding:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:10, flexWrap:"wrap" }}>
              {/* Tasto timbra a SINISTRA */}
              {!cartellinoBloccato && (
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>setModalTimbra("entrata")}
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"10px 16px", border:"none", borderRadius:10, background:"#059669", color:"#fff", fontWeight:800, fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif", boxShadow:"0 4px 14px rgba(5,150,105,.3)", flexShrink:0 }}>
                    <LogIn size={15}/> Entrata
                  </button>
                  <button onClick={()=>setModalTimbra("uscita")}
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"10px 16px", border:"none", borderRadius:10, background:"#dc2626", color:"#fff", fontWeight:800, fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif", boxShadow:"0 4px 14px rgba(220,38,38,.3)", flexShrink:0 }}>
                    <LogOut size={15}/> Uscita
                  </button>
                </div>
              )}
              <div>
                <div style={{ fontSize:10, color: cartellinoBloccato?"#6b7280":"#7c3aed", fontWeight:700 }}>OGGI — {oggi.toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long"})} {cartellinoBloccato && `· ${STATI_PRES[statoOggi].label}`}</div>
              </div>
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {(presenzaOggi?.timbrature||[]).length===0 ? (
                <span style={{ fontSize:12, color:"#9ca3af" }}>Nessuna timbratura oggi</span>
              ) : presenzaOggi.timbrature.map((t,i)=>{
                const colSpec = t.speciale ? COLORI_SPECIALI[t.commessaNome] : null;
                return (
                  <span key={i} style={{ padding:"3px 10px", borderRadius:12, fontSize:12, fontWeight:700, background: colSpec ? colSpec+"22" : (t.tipo==="entrata"?"#d1fae5":"#fee2e2"), color: colSpec || (t.tipo==="entrata"?"#059669":"#dc2626") }}>
                    {t.speciale ? `🏷️ ${t.commessaNome}` : (t.tipo==="entrata"?"→ Entrata":"← Uscita")} {!t.speciale && t.ora}{t.commessaNome&&!t.speciale?` · ${t.commessaNome}`:""}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        {sonoEscluso && (
          <div style={{ background:"#f3f4f6", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#6b7280" }}>Non sei tenuto a timbrare la presenza.</div>
        )}

        <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:14 }}>
          <button onClick={()=>{ if(mese===0){setMese(11);setAnno(a=>a-1);}else setMese(m=>m-1); }} style={{ border:"1px solid #e5e7eb", background:"#fff", borderRadius:6, padding:"3px 8px", cursor:"pointer" }}><ChevronLeft size={14}/></button>
          <span style={{ fontSize:14, fontWeight:700, minWidth:150, textAlign:"center" }}>{MESI[mese]} {anno}</span>
          <button onClick={()=>{ if(mese===11){setMese(0);setAnno(a=>a+1);}else setMese(m=>m+1); }} style={{ border:"1px solid #e5e7eb", background:"#fff", borderRadius:6, padding:"3px 8px", cursor:"pointer" }}><ChevronRight size={14}/></button>
        </div>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:16 }}>
        <div style={{ display:"grid", gap:8 }}>
          {utentiAttivi.map(u=>(
            <ListaUtenteCartellino key={u.uid} utente={u} anno={anno} mese={mese} orgId={effectiveOrgId} onApri={()=>setVistaCartellino({ uid:u.uid, nome:u.displayName })}/>
          ))}
        </div>
      </div>

      {modalTimbra && (
        <ModalTimbra tipo={modalTimbra} commesse={commesse} diario={!!usaDiarioMio} onConferma={(ora,cId,cNome,extra)=>timbra(modalTimbra, ora, cId, cNome, extra)} onClose={()=>setModalTimbra(null)}
          bloccaFeriePermesso={bloccaFeriePermesso} giorniMancantiCount={mancantiPrecedenti.length}/>
      )}

      {showPromemoriaDDT && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowPromemoriaDDT(false)}>
          <div className="modal" style={{ maxWidth:380 }}>
            <div style={{ textAlign:"center", padding:"8px 4px" }}>
              <div style={{ fontSize:32, marginBottom:10 }}>📦</div>
              <h3 style={{ fontSize:15, fontWeight:700, marginBottom:8 }}>Hai ricevuto materiali in cantiere in questi giorni?</h3>
              <p style={{ fontSize:12, color:"#6b7280", marginBottom:18 }}>Se hai firmato un DDT da un corriere o da un fornitore, ricordati di caricarne la foto in Nexiora.</p>
              <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
                <button onClick={()=>setShowPromemoriaDDT(false)} className="btn btn-secondary" style={{ fontSize:13 }}>
                  No, salta
                </button>
                <button onClick={()=>{ setShowPromemoriaDDT(false); onNavigate && onNavigate({page:"registro-ddt-ingresso"}); }}
                  className="btn btn-primary" style={{ fontSize:13 }}>
                  📎 Sì, carica i DDT
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPannelloVerifica && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowPannelloVerifica(false)}>
          <div className="modal" style={{ maxWidth:460 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <h3 style={{ fontSize:14, fontWeight:700 }}>Cartellini da verificare</h3>
              <button onClick={()=>setShowPannelloVerifica(false)} style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af" }}><X size={16}/></button>
            </div>
            <p style={{ fontSize:11, color:"#6b7280", marginBottom:12 }}>Mesi chiusi non ancora approvati. Scadenza verifica: giorno 10 del mese successivo al mese di riferimento.</p>
            <div style={{ maxHeight:360, overflowY:"auto", display:"grid", gap:6 }}>
              {cartelliniDaVerificare.map(p=>{
                const u = utenti.find(x=>x.uid===p.uid);
                return (
                  <button key={p.id} onClick={()=>{
                    setAnno(p.anno); setMese((p.mese||1)-1);
                    setVistaCartellino({ uid:p.uid, nome:u?.displayName||"—" });
                    setShowPannelloVerifica(false);
                  }} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"9px 12px", border:`1px solid ${p.scaduto?"#fde68a":"#e5e7eb"}`, background:p.scaduto?"#fffbeb":"#fff", borderRadius:8, cursor:"pointer", fontFamily:"'Inter',sans-serif", textAlign:"left" }}>
                    <span style={{ fontSize:12, fontWeight:600, color:"#111827" }}>{u?.displayName||"Utente"} — {MESI[(p.mese||1)-1]} {p.anno}</span>
                    {p.scaduto && <span style={{ fontSize:10, fontWeight:700, color:"#92400e" }}>⚠️ scaduto</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showGestioneEsclusioni && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowGestioneEsclusioni(false)}>
          <div className="modal" style={{ maxWidth:420 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ fontSize:14, fontWeight:700 }}>Gestisci esclusioni timbratura</h3>
              <button onClick={()=>setShowGestioneEsclusioni(false)} style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af" }}><X size={16}/></button>
            </div>
            <p style={{ fontSize:12, color:"#6b7280", marginBottom:14 }}>Gli utenti selezionati non sono tenuti a timbrare la presenza.</p>
            <div style={{ maxHeight:300, overflowY:"auto" }}>
              {utenti.map(u=>(
                <label key={u.uid} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:6, cursor:"pointer", fontSize:13 }}>
                  <input type="checkbox" checked={esclusi.includes(u.uid)} onChange={()=>toggleEsclusione(u.uid)}/>
                  {u.displayName}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ListaUtenteCartellino({ utente, anno, mese, orgId, onApri }) {
  const [cartData, setCartData] = useState(null);
  const docId = `${orgId}_${utente.uid}_${anno}_${mese+1}`;
  useEffect(()=>{ if(!orgId) return; return onSnapshot(doc(db,"presenze",docId), snap=>{ setCartData(snap.exists() ? snap.data() : { giorni:{}, stato:"bozza" }); }); },[docId,orgId]);

  const presenze = cartData?.giorni || {};
  const stato = cartData?.stato || "bozza";
  const daVerificare = meseChiuso(anno, mese) && stato !== "approvata";
  const statoEffettivo = daVerificare && scadenzaVerificaSuperata(anno, mese) ? "chiuso_non_verificato" : stato;

  const { totaleMinuti, giorniLavorati, anomalie } = useMemo(()=>{
    let tot=0, giorni=0, anom=0;
    Object.values(presenze).forEach(g=>{
      const r = calcolaOreGiorno(g.timbrature||[]);
      if((g.timbrature||[]).length>0) giorni++;
      tot += r.minutiLavorati + r.minutiSpeciali;
      if(r.mancaUscita) anom++;
    });
    return { totaleMinuti:tot, giorniLavorati:giorni, anomalie:anom };
  },[presenze]);

  return (
    <div onClick={onApri} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer", transition:"box-shadow .15s" }}
      onMouseOver={e=>e.currentTarget.style.boxShadow="0 2px 12px rgba(124,58,237,.1)"} onMouseOut={e=>e.currentTarget.style.boxShadow="none"}>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <div style={{ width:36, height:36, borderRadius:"50%", background:"#ede9fe", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:"#7c3aed" }}>
          {(utente.displayName||"?").split(" ").map(s=>s[0]).join("").slice(0,2).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{utente.displayName}</div>
          <div style={{ fontSize:11, color:"#9ca3af" }}>{giorniLavorati} giorni · <span style={{ color:STATI_PRES[statoEffettivo].colore, fontWeight:600 }}>{STATI_PRES[statoEffettivo].label}</span></div>
        </div>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
        {anomalie>0 && <span style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#dc2626", fontWeight:600 }}><AlertTriangle size={12}/> {anomalie}</span>}
        <div style={{ textAlign:"right" }}>
          <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700 }}>TOTALE</div>
          <div style={{ fontSize:15, fontWeight:800, color:"#059669" }}>{fmtOreDecimali(totaleMinuti)}</div>
        </div>
        <ChevronRight size={16} color="#9ca3af"/>
      </div>
    </div>
  );
}
